<template>
  <main :class="['home-prototype', 'single-film-prototype', 'single-person-prototype', `home-prototype--${currentContentType}`]">
    <section class="home-top-panel g-0 position-relative">
      <HeroTopPanel
        :logo-src="logoPath"
        login-path="/login?redirect=%2Fhome"
        :show-background="false"
        content-class="home-top-panel__content"
        @open-mobile-search="openMobileSearchOverlay"
      >
        <div class="row g-0 home-tabs-row">
          <div class="col">
            <h1
              v-if="isReadOnlyProfile"
              class="home-public-profile-title"
              data-testid="home-public-profile-username"
            >
              {{ readOnlyProfileDisplayName }}
            </h1>
            <nav v-else class="home-tabs" aria-label="Home sections">
              <a
                v-for="tab in tabs"
                :key="tab.key"
                :href="tab.href"
                :class="['home-tabs__link', { 'home-tabs__link--active': tab.active }]"
              >
                {{ tab.label }}
              </a>
            </nav>
          </div>
        </div>
        <div v-if="!isSettingsSection" class="row g-0 home-content-tabs-row">
          <div class="col">
            <nav class="home-content-tabs" aria-label="Home content types">
              <a
                v-for="contentTab in contentTabs"
                :key="contentTab.key"
                :href="contentTab.href"
                :class="['home-content-tabs__link', { 'home-content-tabs__link--active': contentTab.active }]"
                :data-testid="`home-content-tab-${contentTab.key}`"
                @click.prevent="setContentType(contentTab.key)"
              >
                {{ contentTab.label }}
              </a>
            </nav>
          </div>
        </div>
        <div v-if="!isSettingsSection" class="row g-0 home-watch-counts-row">
          <div class="col">
            <p class="home-watch-counts" data-testid="home-watch-counts">
              <span data-testid="home-watch-counts-line">
                {{ watchCounts.films.uniqueWatched }} watched films
                <span aria-hidden="true">•</span>
                {{ watchCounts.films.watches }} watches
              </span>
              <span data-testid="home-watch-counts-line">
                {{ watchCounts.episodes.uniqueWatched }} watched episodes
                <span aria-hidden="true">•</span>
                {{ watchCounts.episodes.watches }} watches
              </span>
            </p>
          </div>
        </div>
      </HeroTopPanel>
    </section>

    <section class="home-content-section">
      <div class="container g-0">
        <div
          v-if="isSettingsSection"
          class="row g-0 home-settings-layout"
          data-testid="home-settings-layout"
        >
          <div class="col-5 g-0 home-settings-content">
            <section
              v-if="settingsPanel === 'account'"
              class="home-settings-panel"
              data-testid="home-settings-account-panel"
            >
              <h2 class="home-section-title">Account settings</h2>
              <p v-if="accountSettingsLoading" class="home-empty-state">Loading account settings...</p>
              <p v-else-if="accountSettingsError" class="home-settings-message home-settings-message--error">
                {{ accountSettingsError }}
              </p>
              <template v-else>
                <form
                  class="home-settings-form"
                  data-testid="home-settings-email-form"
                  @submit.prevent="submitEmailChange"
                >
                  <h3 class="home-settings-subtitle">Email address</h3>
                  <p class="home-settings-copy">
                    Change the address used for login and account messages. Email confirmation will be added later.
                  </p>
                  <div class="home-settings-email-row">
                    <p
                      v-if="!accountEmailEditing"
                      class="home-settings-email-value"
                      data-testid="home-settings-email-value"
                    >
                      {{ accountEmail }}
                    </p>
                    <button
                      class="sorting-btn filter-btn home-settings-button home-settings-edit-button"
                      data-testid="home-settings-email-edit"
                      type="button"
                      @click="accountEmailEditing ? cancelEmailEdit() : startEmailEdit()"
                    >
                      {{ accountEmailEditing ? 'Cancel' : 'Edit' }}
                    </button>
                  </div>
                  <template v-if="accountEmailEditing">
                    <label class="home-settings-field">
                      <span>New email</span>
                      <input
                        v-model.trim="accountEmailForm.newEmail"
                        data-testid="home-settings-new-email"
                        type="email"
                        autocomplete="email"
                        required
                      >
                    </label>
                    <label class="home-settings-field">
                      <span>Retype email</span>
                      <input
                        v-model.trim="accountEmailForm.newEmailConfirm"
                        data-testid="home-settings-retype-email"
                        type="email"
                        autocomplete="email"
                        required
                      >
                    </label>
                    <div class="home-settings-actions">
                      <button
                        class="sorting-btn filter-btn home-settings-button home-settings-submit"
                        data-testid="home-settings-email-save"
                        type="submit"
                        :disabled="accountEmailSaveDisabled"
                      >
                        {{ accountEmailSaving ? 'Saving...' : 'Save' }}
                      </button>
                    </div>
                  </template>
                  <p
                    v-if="accountEmailMessage"
                    class="home-settings-message"
                    data-testid="home-settings-email-message"
                  >
                    {{ accountEmailMessage }}
                  </p>
                  <p
                    v-if="accountEmailError"
                    class="home-settings-message home-settings-message--error"
                    data-testid="home-settings-email-error"
                  >
                    {{ accountEmailError }}
                  </p>
                </form>

                <form
                  class="home-settings-form"
                  data-testid="home-settings-password-form"
                  @submit.prevent="submitPasswordChange"
                >
                  <h3
                    class="home-settings-subtitle"
                    data-testid="home-settings-password-title"
                  >
                    Change password
                  </h3>
                  <label v-if="accountHasUsablePassword" class="home-settings-field">
                    <span>Current password</span>
                    <input
                      v-model="accountPasswordForm.currentPassword"
                      data-testid="home-settings-current-password"
                      type="password"
                      autocomplete="current-password"
                      required
                    >
                  </label>
                  <label class="home-settings-field">
                    <span>New password</span>
                    <input
                      v-model="accountPasswordForm.newPassword"
                      data-testid="home-settings-new-password"
                      type="password"
                      autocomplete="new-password"
                      required
                    >
                  </label>
                  <p
                    class="home-settings-help"
                    data-testid="home-settings-password-rules"
                  >
                    At least 10 characters, one number, and one special character.
                  </p>
                  <label class="home-settings-field">
                    <span>Confirm new password</span>
                    <input
                      v-model="accountPasswordForm.newPasswordConfirm"
                      data-testid="home-settings-new-password-confirm"
                      type="password"
                      autocomplete="new-password"
                      required
                    >
                  </label>
                  <p
                    v-if="accountPasswordMessage"
                    class="home-settings-message"
                    data-testid="home-settings-password-message"
                  >
                    {{ accountPasswordMessage }}
                  </p>
                  <p
                    v-if="accountPasswordError"
                    class="home-settings-message home-settings-message--error"
                    data-testid="home-settings-password-error"
                  >
                    {{ accountPasswordError }}
                  </p>
                  <div class="home-settings-actions">
                    <button
                      class="sorting-btn filter-btn home-settings-button home-settings-submit"
                      data-testid="home-settings-password-submit"
                      type="submit"
                      :disabled="accountPasswordSaveDisabled"
                    >
                      {{ accountPasswordSaving ? 'Saving...' : 'Save' }}
                    </button>
                  </div>
                </form>
              </template>
            </section>

            <section
              v-else
              class="home-settings-panel"
              data-testid="home-settings-removal-panel"
            >
              <h2 class="home-section-title">Account removal</h2>
              <p class="home-settings-copy">
                Request account removal. This records the request locally so it can be reviewed before anything is deleted.
              </p>
              <label v-if="!accountRemovalRequestedAt" class="home-settings-field">
                <span>Reason for removal</span>
                <textarea
                  v-model.trim="accountRemovalReason"
                  data-testid="home-settings-removal-reason"
                  rows="4"
                />
              </label>
              <p
                v-if="accountRemovalRequestedAt"
                class="home-settings-message"
                data-testid="home-settings-removal-message"
              >
                Account removal is pending.
              </p>
              <p
                v-if="accountRemovalError"
                class="home-settings-message home-settings-message--error"
                data-testid="home-settings-removal-error"
              >
                {{ accountRemovalError }}
              </p>
              <div class="home-settings-actions">
                <button
                  v-if="accountRemovalRequestedAt"
                  class="sorting-btn filter-btn home-settings-button home-settings-submit"
                  data-testid="home-settings-removal-cancel"
                  type="button"
                  :disabled="accountRemovalSaving"
                  @click="cancelAccountRemovalRequest"
                >
                  {{ accountRemovalSaving ? 'Cancelling...' : 'Cancel removal' }}
                </button>
                <button
                  v-else
                  class="sorting-btn filter-btn home-settings-button home-settings-submit home-settings-danger-button"
                  data-testid="home-settings-removal-submit"
                  type="button"
                  :disabled="accountRemovalSaving"
                  @click="submitAccountRemovalRequest"
                >
                  {{ accountRemovalSaving ? 'Requesting...' : 'Request removal' }}
                </button>
              </div>
            </section>
          </div>
          <aside class="col-2 g-0 home-settings-links" data-testid="home-settings-links">
            <a
              v-for="link in settingsLinks"
              :key="link.key"
              :href="link.href"
              :class="{ active: settingsPanel === link.key }"
              @click="handleSettingsLinkClick(link, $event)"
            >
              {{ link.label }}
            </a>
            <p
              v-if="accountGoogleMessage"
              class="home-settings-message home-settings-link-message"
              data-testid="home-settings-google-message"
            >
              {{ accountGoogleMessage }}
            </p>
            <p
              v-if="accountGoogleError"
              class="home-settings-message home-settings-message--error home-settings-link-message"
              data-testid="home-settings-google-error"
            >
              {{ accountGoogleError }}
            </p>
          </aside>
        </div>
        <div v-else class="row g-0 film-overview home-overview">
          <div class="col-9 g-0" data-testid="home-overview-main">
            <div class="container g-0">
              <div class="row g-0 sorting-buttons-row">
                <div class="col g-0">
                  <h2 class="home-section-title" data-testid="home-watched-films-heading">
                    {{ filmStatusHeading }}
                  </h2>
                  <div
                    v-if="filmStatusHasSortControls"
                    :class="['home-sort-controls', `home-sort-controls--${currentFilmStatus}`]"
                    data-testid="home-sort-controls"
                  >
                    <span class="home-sort-controls__title">Sort:</span>
                    <template
                      v-for="button in sortButtons"
                      :key="button.key"
                    >
                      <span
                        v-if="button.key === 'oldest'"
                        class="home-sort-controls__mobile-break"
                        aria-hidden="true"
                      ></span>
                      <span
                        v-if="currentFilmStatus === 'watched' && button.key === 'title-asc'"
                        class="home-sort-controls__tablet-title-break"
                        aria-hidden="true"
                      ></span>
                      <span
                        v-if="currentFilmStatus === 'next' && button.key === 'air-date-desc'"
                        class="home-sort-controls__next-air-date-break"
                        aria-hidden="true"
                      ></span>
                      <button
                        :class="[
                          'view-mode-btn',
                          'sorting-btn',
                          `home-sort-button--${button.key}`,
                          button.active ? 'active' : null,
                        ]"
                        data-testid="home-sort-button"
                        type="button"
                        @click="setSortMode(button.key)"
                      >
                        {{ button.label }}
                      </button>
                    </template>
                    <button
                      v-if="homeFilterButtonVisible"
                      class="sorting-btn filter-btn home-filter-btn"
                      data-testid="home-filter-button"
                      type="button"
                      @click="openHomeFiltersModal"
                    >
                      Filters
                    </button>
                  </div>
                  <div
                    v-if="filmStatusHasSortControls"
                    :class="[
                      'home-sort-controls-mobile',
                      `home-sort-controls-mobile--${currentFilmStatus}`,
                    ]"
                    data-testid="home-mobile-sort-controls"
                  >
                    <div class="home-sort-controls-mobile__title-row">
                      <span class="home-sort-controls__title">Sort:</span>
                    </div>
                    <div
                      v-for="(row, rowIndex) in mobileSortButtonRows"
                      :key="`mobile-sort-row-${rowIndex}`"
                      :class="[
                        'home-sort-controls-mobile__button-row',
                        `home-sort-controls-mobile__button-row--${rowIndex + 1}`,
                      ]"
                      data-testid="home-mobile-sort-row"
                    >
                      <template
                        v-for="button in row"
                        :key="`mobile-${button.key}`"
                      >
                        <span
                          v-if="button.key === 'watch-date-asc'"
                          class="home-sort-controls-mobile__watch-date-break"
                          aria-hidden="true"
                        ></span>
                        <button
                          :class="[
                            'view-mode-btn',
                            'sorting-btn',
                            `home-sort-button--${button.key}`,
                            button.active ? 'active' : null,
                          ]"
                          data-testid="home-mobile-sort-button"
                          type="button"
                          @click="setSortMode(button.key)"
                        >
                          {{ button.label }}
                        </button>
                      </template>
                      <button
                        v-if="homeFilterButtonVisible && rowIndex === mobileSortButtonRows.length - 1"
                        class="sorting-btn filter-btn home-filter-btn home-filter-btn--mobile-rows"
                        data-testid="home-mobile-filter-button"
                        type="button"
                        @click="openHomeFiltersModal"
                      >
                        Filters
                      </button>
                    </div>
                  </div>
                  <div
                    v-if="homeViewModeControlsVisible"
                    class="home-view-mode-row"
                    data-testid="home-view-mode-row"
                  >
                    <button
                      v-for="button in homeViewModeButtons"
                      :key="`home-view-mode-${button.key}`"
                      :class="[
                        'view-mode-btn',
                        'sorting-btn',
                        button.active ? 'active' : null,
                      ]"
                      :aria-label="button.label"
                      :data-testid="`home-view-mode-${button.key}`"
                      :title="button.label"
                      type="button"
                      @click="setHomeViewMode(button.key)"
                    >
                      <ViewModeIcon :mode="button.key" data-testid="home-view-mode-icon" />
                    </button>
                    <button
                      v-if="homeViewModeFilterButtonVisible"
                      class="sorting-btn filter-btn home-filter-btn home-filter-btn--view-row"
                      aria-label="Filters"
                      data-testid="home-view-filter-button"
                      type="button"
                      @click="openHomeFiltersModal"
                    >
                      Filters
                    </button>
                  </div>
                </div>
              </div>

              <p v-if="watchedFilmsLoading" class="home-empty-state">Loading...</p>
              <div
                v-else-if="currentContentType === 'lists'"
                class="home-lists-panel"
                data-testid="home-lists-panel"
              >
                <p
                  v-if="listDeletedNotice"
                  class="home-lists-notice"
                  data-testid="home-lists-deleted-notice"
                  @click="listDeletedNotice = ''"
                >
                  {{ listDeletedNotice }}
                </p>
                <div v-if="!isReadOnlyProfile" class="home-lists-actions">
                  <button
                    v-if="!isReadOnlyProfile"
                    class="btn-sm list-addition-btn home-lists-add-button"
                    data-testid="home-lists-add-button"
                    type="button"
                    @click="openHomeListModal(selectedHomeListType.key)"
                  >
                    Add new list
                  </button>
                </div>
                <p v-if="homeListsLoading" class="home-empty-state">Loading lists...</p>
                <p v-else-if="homeListsError" class="home-empty-state" data-testid="home-lists-error">
                  {{ homeListsError }}
                </p>
                <div
                  v-else-if="homeLists.length"
                  class="film-list-stack film-list-stack--page"
                  data-testid="home-lists-list"
                >
                  <div
                    v-for="listItem in homeLists"
                    :key="`${listItem.type}-${listItem.id}`"
                    :class="[
                      'film-list',
                      'film-list--page',
                      `home-list-row--${listItem.type}`,
                    ]"
                    data-testid="home-list-row"
                  >
                    <div class="list-posters">
                      <div
                        v-for="item in listItem.items || []"
                        :key="item.id"
                        class="list-poster"
                      >
                        <a :href="listItem.url || '#'">
                          <img :src="item.poster_url" :alt="`${item.title} poster`">
                        </a>
                      </div>
                    </div>
                    <a class="list-title" :href="listItem.url || '#'">
                      {{ listItem.title }}
                    </a>
                    <p class="list-owner" data-testid="home-list-row-meta">
                      {{ listItem.item_count }} {{ homeListItemCountLabel(listItem) }}
                      <span v-if="!listItem.public"> • Private</span>
                    </p>
                    <p
                      v-if="listItem.description"
                      class="home-list-description"
                      data-testid="home-list-row-description"
                    >
                      <span data-testid="home-list-row-description-body">
                        {{ displayedHomeListDescription(listItem) }}
                      </span>
                      <button
                        v-if="hasExpandableHomeListDescription(listItem)"
                        class="overview-toggle"
                        data-testid="home-list-row-description-toggle"
                        type="button"
                        @click="toggleHomeListDescription(listItem)"
                      >
                        <span class="overview-toggle__icon" aria-hidden="true">
                          {{ isHomeListDescriptionExpanded(listItem) ? '▲' : '▼' }}
                        </span>
                      </button>
                    </p>
                  </div>
                </div>
                <div v-else class="home-lists-empty-state">
                  <p class="home-empty-state" data-testid="home-lists-empty">
                    No {{ selectedHomeListType.label.toLowerCase() }} lists, yet.
                  </p>
                </div>
                <nav
                  v-if="!homeListsLoading && !homeListsError && homeListsNumPages > 1"
                  class="home-pagination home-pagination--with-mobile"
                  aria-label="Home lists pagination"
                >
                  <ul class="pagination mb-0">
                    <li class="page-item" :class="{ disabled: !canGoToPreviousHomeListsPage || homeListsLoading }">
                      <button
                        class="page-link"
                        data-testid="home-lists-prev"
                        type="button"
                        @click="goToHomeListsPage(homeListsPage - 1)"
                      >
                        Previous
                      </button>
                    </li>
                    <li
                      v-for="pageItem in homeListsPages"
                      :key="`home-lists-${pageItem.key}`"
                      class="page-item"
                      :class="{ active: pageItem.page === homeListsPage, disabled: pageItem.ellipsis }"
                    >
                      <span v-if="pageItem.ellipsis" class="page-link" aria-hidden="true">
                        {{ pageItem.label }}
                      </span>
                      <button
                        v-else
                        class="page-link"
                        :data-testid="`home-lists-page-${pageItem.page}`"
                        type="button"
                        :aria-label="`Go to page ${pageItem.page}`"
                        @click="goToHomeListsPage(pageItem.page)"
                      >
                        {{ pageItem.label }}
                      </button>
                    </li>
                    <li class="page-item" :class="{ disabled: !canGoToNextHomeListsPage || homeListsLoading }">
                      <button
                        class="page-link"
                        data-testid="home-lists-next"
                        type="button"
                        @click="goToHomeListsPage(homeListsPage + 1)"
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                  <CompactMobilePagination
                    :current-page="homeListsPage"
                    :total-pages="homeListsNumPages"
                    :loading="homeListsLoading"
                    test-id-prefix="home-lists"
                    @page-change="goToHomeListsPage"
                  />
                </nav>
              </div>
              <p v-else-if="watchedFilmsError" class="home-empty-state">{{ watchedFilmsError }}</p>
              <p v-else-if="watchedFilms.length === 0" class="home-empty-state">
                No {{ contentTypeLabel[currentContentType] || 'items' }} yet.
              </p>

              <div
                v-else-if="homeDetailsViewVisible"
                class="home-detail-list"
                data-testid="home-watched-films-detail-list"
              >
                <div
                  v-for="film in watchedFilms"
                  :key="`detail-${homeItemKey(film)}`"
                  :class="[
                    'home-detail-row',
                    {
                      'home-detail-row--episode': currentMedium === 'tv',
                      'home-list-row--bordered': !isLastHomeItem(film),
                    },
                  ]"
                  :data-testid="`home-${homeStatusTestPrefix}-film-detail-row`"
                >
                  <a class="home-detail-poster-link" :href="film.url || '#'">
                    <img
                      :class="[
                        'home-detail-poster',
                        { 'home-detail-poster--episode': currentMedium === 'tv' },
                      ]"
                      :data-testid="`home-${homeStatusTestPrefix}-film-detail-poster`"
                      :src="posterImageUrl(film)"
                      :alt="itemImageAlt(film)"
                    >
                  </a>
                  <div class="home-detail-content">
                    <div
                      v-if="currentMedium === 'tv'"
                      class="home-detail-title-row home-episode-detail-title-row"
                    >
                      <a
                        class="home-detail-title"
                        data-testid="home-watched-episode-detail-show-title"
                        :href="film.url || '#'"
                      >
                        {{ film.show_title }}
                      </a>
                      <a
                        class="home-detail-subtitle home-episode-title-line"
                        data-testid="home-watched-episode-detail-episode-title"
                        :href="film.url || '#'"
                      >
                        {{ episodeTitleLine(film) }}
                      </a>
                    </div>
                    <div
                      v-else
                      class="home-detail-title-row"
                    >
                      <a
                        class="home-detail-title"
                        :data-testid="`home-${homeStatusTestPrefix}-film-detail-title`"
                        :href="film.url || '#'"
                      >
                        {{ film.title }}
                        <span v-if="film.year" class="home-detail-year">({{ film.year }})</span>
                      </a>
                      <p
                        v-if="film.subtitle"
                        class="home-detail-subtitle"
                        :data-testid="`home-${homeStatusTestPrefix}-film-detail-subtitle`"
                      >
                        {{ film.subtitle }}
                      </p>
                    </div>
                    <p
                      v-if="homeDetailDate(film)"
                      class="home-detail-meta"
                      :data-testid="`home-${homeStatusTestPrefix}-film-detail-date`"
                    >
                      {{ homeDetailDate(film) }}
                    </p>
                    <p
                      v-if="currentFilmStatus === 'next' && film.overview"
                      class="home-detail-description"
                      data-testid="home-next-film-detail-overview"
                    >
                      <span>{{ displayedNextEpisodeOverview(film) }}</span>
                      <button
                        v-if="hasExpandableNextEpisodeOverview(film)"
                        class="overview-toggle"
                        data-testid="home-next-film-detail-overview-toggle"
                        type="button"
                        @click="toggleNextEpisodeOverview(film)"
                      >
                        <span class="overview-toggle__icon" aria-hidden="true">
                          {{ isNextEpisodeOverviewExpanded(film) ? '▲' : '▼' }}
                        </span>
                      </button>
                    </p>
                    <div
                      v-if="currentFilmStatus === 'rated' && film.rating"
                      class="home-inline-rating"
                      data-testid="home-rated-film-detail-rating"
                    >
                      <i class="home-inline-rating__star ion-ios-star" aria-hidden="true"></i>
                      <span class="home-inline-rating__value">{{ film.rating }}</span>
                    </div>
                    <p
                      v-if="currentFilmStatus === 'watched' && film.format"
                      class="home-detail-meta"
                      data-testid="home-watched-film-detail-format"
                    >
                      {{ film.format }}
                    </p>
                    <p
                      v-if="currentFilmStatus === 'watched' && film.description"
                      class="home-detail-description home-detail-description--inline"
                      data-testid="home-watched-film-detail-description"
                    >
                      {{ film.description }}
                    </p>
                  </div>
                  <p
                    v-if="currentMedium === 'tv' && currentFilmStatus === 'watched' && film.description"
                    class="home-detail-description home-episode-detail-description-mobile"
                    data-testid="home-watched-episode-detail-description-mobile"
                  >
                    {{ film.description }}
                  </p>
                </div>
              </div>

              <div
                v-else-if="homeTextOnlyViewVisible"
                class="home-text-list"
                data-testid="home-watched-films-text-list"
              >
                <div
                  v-for="film in watchedFilms"
                  :key="`text-${homeItemKey(film)}`"
                  :class="[
                    'home-text-row',
                    {
                      'home-list-row--bordered': !isLastHomeItem(film),
                      'home-text-row--rated': currentFilmStatus === 'rated',
                    },
                  ]"
                  :data-testid="`home-${homeStatusTestPrefix}-film-text-row`"
                >
                  <div class="home-text-main">
                    <template v-if="currentMedium === 'tv'">
                      <a
                        class="home-detail-title home-text-title"
                        data-testid="home-watched-episode-text-show-title"
                        :href="film.url || '#'"
                      >
                        {{ film.show_title }}
                      </a>
                      <a
                        class="home-detail-subtitle home-text-subtitle home-episode-title-line"
                        data-testid="home-watched-episode-text-episode-title"
                        :href="film.url || '#'"
                      >
                        {{ episodeTitleLine(film) }}
                      </a>
                    </template>
                    <template v-else>
                      <a
                        class="home-detail-title home-text-title"
                        :data-testid="`home-${homeStatusTestPrefix}-film-text-title`"
                        :href="film.url || '#'"
                      >
                        {{ film.title }}
                        <span v-if="film.year" class="home-detail-year">({{ film.year }})</span>
                      </a>
                      <p
                        v-if="film.subtitle"
                        class="home-detail-subtitle home-text-subtitle"
                        :data-testid="`home-${homeStatusTestPrefix}-film-text-subtitle`"
                      >
                        {{ film.subtitle }}
                      </p>
                    </template>
                    <p
                      v-if="homeTextMeta(film)"
                      class="home-detail-meta home-text-meta"
                      :data-testid="currentFilmStatus === 'watched' ? 'home-watched-film-text-meta' : `home-${homeStatusTestPrefix}-film-text-date`"
                    >
                      {{ homeTextMeta(film) }}
                    </p>
                  </div>
                  <div
                    v-if="currentFilmStatus === 'rated' && film.rating"
                    class="home-inline-rating home-inline-rating--text"
                    data-testid="home-rated-film-text-rating"
                  >
                    <i class="home-inline-rating__star ion-ios-star" aria-hidden="true"></i>
                    <span class="home-inline-rating__value">{{ film.rating }}</span>
                  </div>
                  <p
                    v-if="currentFilmStatus === 'watched' && film.description"
                    class="home-detail-description home-text-description"
                    data-testid="home-watched-film-text-description"
                  >
                    {{ film.description }}
                  </p>
                </div>
              </div>

              <div
                v-else
                :class="[
                  'person-posters-grid',
                  'home-posters-grid',
                  { 'home-posters-grid--episodes': currentMedium === 'tv' },
                ]"
                data-testid="home-watched-films-grid"
              >
                <div
                  v-for="film in watchedFilms"
                  :key="`watch-${homeItemKey(film)}`"
                  class="person-poster-card"
                  data-testid="home-watched-film-poster"
                >
                  <a class="person-poster-link" :href="film.url || '#'">
                    <img
                      class="person-poster-grid-image"
                      :src="posterImageUrl(film)"
                      :alt="itemImageAlt(film)"
                    >
                    <div
                      v-if="shouldShowPlaceholderOverlay(film)"
                      class="person-poster-placeholder-overlay"
                      data-testid="home-poster-placeholder-overlay"
                    >
                      <h3 class="person-poster-placeholder-title">{{ film.title }}</h3>
                      <p
                        v-if="film.subtitle || film.year"
                        class="person-poster-placeholder-year"
                      >
                        {{ film.subtitle || film.year }}
                      </p>
                    </div>
                    <div
                      v-if="currentFilmStatus === 'rated' && film.rating"
                      :class="[
                        'home-rating-poster-overlay',
                        { 'home-episode-rating-poster-overlay': currentMedium === 'tv' },
                      ]"
                      data-testid="home-rating-poster-overlay"
                    >
                      <template v-if="currentMedium === 'tv'">
                        <span class="home-episode-rating-poster-overlay__title-col">
                          <span
                            class="home-episode-poster-overlay__show-title"
                            data-testid="home-episode-rating-overlay-show-title"
                          >
                            {{ film.show_title }}
                          </span>
                          <span
                            class="home-episode-rating-poster-overlay__episode-title"
                            data-testid="home-episode-rating-overlay-episode-title"
                          >
                            {{ episodeTitleLine(film) }}
                          </span>
                        </span>
                        <span class="home-episode-rating-poster-overlay__rating-col">
                          <i
                            class="home-rating-poster-overlay__star ion-ios-star"
                            aria-hidden="true"
                          ></i>
                          <span
                            class="home-rating-poster-overlay__value"
                            data-testid="home-episode-rating-overlay-value"
                          >
                            {{ film.rating }}
                          </span>
                        </span>
                      </template>
                      <template v-else>
                        <i
                          class="home-rating-poster-overlay__star ion-ios-star"
                          aria-hidden="true"
                        ></i>
                        <span class="home-rating-poster-overlay__value">
                          {{ film.rating }}
                        </span>
                      </template>
                    </div>
                    <div
                      v-if="currentFilmStatus === 'favourited' && currentMedium === 'tv'"
                      class="home-rating-poster-overlay home-episode-rating-poster-overlay"
                      data-testid="home-favourited-episode-overlay"
                    >
                      <span class="home-episode-rating-poster-overlay__title-col">
                        <span
                          class="home-episode-poster-overlay__show-title"
                          data-testid="home-favourited-episode-overlay-show-title"
                        >
                          {{ film.show_title }}
                        </span>
                        <span
                          class="home-episode-rating-poster-overlay__episode-title"
                          data-testid="home-favourited-episode-overlay-episode-title"
                        >
                          {{ episodeTitleLine(film) }}
                        </span>
                      </span>
                    </div>
                    <div
                      v-if="currentFilmStatus === 'next' && currentMedium === 'tv'"
                      class="home-rating-poster-overlay home-episode-rating-poster-overlay"
                      data-testid="home-next-episode-overlay"
                    >
                      <span class="home-episode-rating-poster-overlay__title-col">
                        <span
                          class="home-episode-poster-overlay__show-title"
                          data-testid="home-next-episode-overlay-show-title"
                        >
                          {{ film.show_title }}
                        </span>
                        <span
                          class="home-episode-rating-poster-overlay__episode-title"
                          data-testid="home-next-episode-overlay-episode-title"
                        >
                          {{ episodeTitleLine(film) }}
                        </span>
                        <span
                          v-if="film.air_date_display"
                          class="home-episode-rating-poster-overlay__episode-title"
                          data-testid="home-next-episode-overlay-date"
                        >
                          {{ film.air_date_display }}
                        </span>
                      </span>
                    </div>
                    <div
                      v-if="currentFilmStatus === 'watched' && watchedOverlayDate(film)"
                      :class="[
                        'home-rating-poster-overlay',
                        'home-date-poster-overlay',
                        { 'home-episode-poster-overlay': currentMedium === 'tv' },
                      ]"
                      data-testid="home-date-poster-overlay"
                    >
                      <template v-if="currentMedium === 'tv'">
                        <span
                          class="home-episode-poster-overlay__show-title"
                          data-testid="home-episode-overlay-show-title"
                        >
                          {{ film.show_title }}
                        </span>
                        <span class="home-episode-poster-overlay__meta-row">
                          <span
                            class="home-episode-poster-overlay__episode-code"
                            data-testid="home-episode-overlay-code"
                          >
                            {{ episodeCode(film) }}
                          </span>
                          <span
                            class="home-date-poster-overlay__value home-episode-poster-overlay__date"
                            data-testid="home-episode-overlay-date"
                          >
                            {{ watchedOverlayDate(film) }}
                          </span>
                        </span>
                      </template>
                      <span
                        v-else
                        class="home-date-poster-overlay__value"
                      >
                        {{ watchedOverlayDate(film) }}
                      </span>
                    </div>
                  </a>
                </div>
              </div>

              <nav
                v-if="watchedFilmsNumPages > 1"
                class="home-pagination home-pagination--with-mobile"
                aria-label="Watched films pagination"
              >
                <ul class="pagination mb-0">
                  <li class="page-item" :class="{ disabled: !canGoToPreviousWatchedFilmsPage || watchedFilmsLoading }">
                    <button
                      class="page-link"
                      data-testid="home-watched-films-prev"
                      type="button"
                      @click="goToWatchedFilmsPage(watchedFilmsPage - 1)"
                    >
                      Previous
                    </button>
                  </li>
                  <li
                    v-for="pageItem in watchedFilmsPages"
                    :key="`home-watched-films-${pageItem.key}`"
                    class="page-item"
                    :class="{ active: pageItem.page === watchedFilmsPage, disabled: pageItem.ellipsis }"
                  >
                    <span v-if="pageItem.ellipsis" class="page-link" aria-hidden="true">
                      {{ pageItem.label }}
                    </span>
                    <button
                      v-else
                      class="page-link"
                      :data-testid="`home-watched-films-page-${pageItem.page}`"
                      type="button"
                      :aria-label="`Go to page ${pageItem.page}`"
                      @click="goToWatchedFilmsPage(pageItem.page)"
                    >
                      {{ pageItem.label }}
                    </button>
                  </li>
                  <li class="page-item" :class="{ disabled: !canGoToNextWatchedFilmsPage || watchedFilmsLoading }">
                    <button
                      class="page-link"
                      data-testid="home-watched-films-next"
                      type="button"
                      @click="goToWatchedFilmsPage(watchedFilmsPage + 1)"
                    >
                      Next
                    </button>
                  </li>
                </ul>
                <CompactMobilePagination
                  :current-page="watchedFilmsPage"
                  :total-pages="watchedFilmsNumPages"
                  :loading="watchedFilmsLoading"
                  test-id-prefix="home-watched-films"
                  @page-change="goToWatchedFilmsPage"
                />
              </nav>
            </div>
          </div>

          <aside
            v-if="filtersPanelVisible"
            class="col-3 g-0 filters-section"
            data-testid="home-filters-sidebar"
          >
            <div class="container person-filters">
              <div class="row g-0 filters-title-row">
                <div class="col g-0">
                  <h3>Filters:</h3>
                </div>
              </div>
              <div v-if="sidebarPrimaryFilters.length" class="row g-0 single-filter-title-row">
                <div class="col g-0">
                  <h3>{{ currentContentType === 'lists' ? 'Type' : 'Status' }}</h3>
                </div>
              </div>
              <div
                v-for="statusFilter in sidebarPrimaryFilters"
                :key="statusFilter.key"
                :class="[
                  'row',
                  'g-0',
                  'filter-row',
                  { 'home-status-filter-row--separated': statusFilter.separated },
                ]"
                :data-testid="`home-status-${statusFilter.key}-row`"
              >
                <div class="col g-0">
                  <a
                    :href="statusFilter.href"
                    :class="{ active: statusFilter.active }"
                    :data-testid="statusFilter.testId"
                    @click.prevent="statusFilter.onClick()"
                  >
                    {{ statusFilter.label }}
                  </a>
                </div>
              </div>
              <div
                v-if="showFiltersVisible"
                class="row g-0 single-filter-title-row"
              >
                <div class="col g-0">
                  <h3>By show</h3>
                </div>
              </div>
              <div
                v-if="showFiltersVisible"
                class="row g-0 filter-row"
              >
                <div class="col g-0">
                  <a
                    href="#"
                    :class="{ active: selectedWatchedShow === '' }"
                    data-testid="home-show-filter-all"
                    @click.prevent="setWatchedShowFilter('')"
                  >
                    All
                  </a>
                </div>
              </div>
              <template v-if="showFiltersVisible">
                <div
                  v-for="showFilter in watchedShowFilters"
                  :key="showFilter.show"
                  class="row g-0 filter-row"
                >
                  <div class="col g-0">
                    <a
                      href="#"
                      :class="{ active: selectedWatchedShow === showFilter.show }"
                      :data-testid="`home-show-filter-${showFilter.show}`"
                      @click.prevent="setWatchedShowFilter(showFilter.show)"
                    >
                      {{ showFilter.title }} ({{ showFilter.count }})
                    </a>
                  </div>
                </div>
                <div
                  v-if="watchedShowDropdownFilters.length"
                  class="row g-0 filter-row"
                >
                  <div class="col g-0">
                    <div class="home-show-filter-dropdown">
                      <button
                        class="home-show-filter-dropdown__toggle"
                        data-testid="home-show-filter-toggle"
                        type="button"
                        aria-haspopup="listbox"
                        :aria-expanded="watchedShowDropdownOpen ? 'true' : 'false'"
                        @click="toggleWatchedShowDropdown"
                      >
                        {{ selectedWatchedShowDropdownLabel }}
                      </button>
                      <div
                        v-if="watchedShowDropdownOpen"
                        class="home-show-filter-dropdown__menu"
                        data-testid="home-show-filter-menu"
                        role="listbox"
                      >
                        <input
                          v-model="watchedShowDropdownQuery"
                          class="home-show-filter-dropdown__search"
                          data-testid="home-show-filter-search"
                          type="text"
                          placeholder="Filter shows"
                          @keydown.escape="closeWatchedShowDropdown"
                        >
                        <button
                          class="home-show-filter-dropdown__option"
                          data-testid="home-show-filter-dropdown-all"
                          type="button"
                          role="option"
                          :aria-selected="selectedWatchedShow === '' ? 'true' : 'false'"
                          @click="selectWatchedShowFromDropdown('')"
                        >
                          {{ allWatchedShowsDropdownLabel }}
                        </button>
                        <button
                          v-for="showFilter in filteredWatchedShowDropdownFilters"
                          :key="`dropdown-${showFilter.show}`"
                          class="home-show-filter-dropdown__option"
                          :class="{ active: selectedWatchedShow === showFilter.show }"
                          :data-testid="`home-show-filter-dropdown-${showFilter.show}`"
                          type="button"
                          role="option"
                          :aria-selected="selectedWatchedShow === showFilter.show ? 'true' : 'false'"
                          @click="selectWatchedShowFromDropdown(showFilter.show)"
                        >
                          {{ showFilter.title }} ({{ showFilter.count }})
                        </button>
                        <p
                          v-if="filteredWatchedShowDropdownFilters.length === 0"
                          class="home-show-filter-dropdown__empty"
                          data-testid="home-show-filter-empty"
                        >
                          No shows found
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <div
                v-if="watchedFormatFiltersVisible"
                class="row g-0 single-filter-title-row"
              >
                <div class="col g-0">
                  <h3>By format</h3>
                </div>
              </div>
              <template v-if="watchedFormatFiltersVisible">
                <div
                  v-for="formatFilter in watchedFormatFilters"
                  :key="`format-${formatFilter.format || 'all'}`"
                  class="row g-0 filter-row"
                >
                  <div class="col g-0">
                    <a
                      :href="homeWatchedFormatFilterHref(formatFilter.format)"
                      :class="{ active: selectedWatchedFormat === formatFilter.format }"
                      :data-testid="`home-format-filter-${formatFilter.format || 'all'}`"
                      @click.prevent="setWatchedFormatFilter(formatFilter.format)"
                    >
                      {{ watchedFormatFilterLabel(formatFilter) }}
                    </a>
                  </div>
                </div>
              </template>
              <div
                v-if="watchedYearFiltersVisible"
                class="row g-0 single-filter-title-row"
              >
                <div class="col g-0">
                  <h3>By year</h3>
                </div>
              </div>
              <div
                v-if="watchedYearFiltersVisible"
                class="row g-0 filter-row"
              >
                <div class="col g-0">
                  <a
                    :href="homeWatchedFilterHref(homeContentPath(currentContentType, 'watched'))"
                    :class="{ active: selectedWatchedYear === '' }"
                    data-testid="home-year-filter-all"
                    @click.prevent="setWatchedYearFilter('')"
                  >
                    All
                  </a>
                </div>
              </div>
              <template v-if="watchedYearFiltersVisible">
                <template
                  v-for="yearFilter in watchedYearFilters"
                  :key="yearFilter.year"
                >
                  <div class="row g-0 filter-row">
                    <div class="col g-0">
                      <a
                        :class="{ active: selectedWatchedYear === yearFilter.year }"
                        :href="homeWatchedFilterHref(homeContentPath(currentContentType, 'watched', yearFilter.year))"
                        :data-testid="`home-year-filter-${yearFilter.year}`"
                        @click.prevent="setWatchedYearFilter(yearFilter.year)"
                      >
                        {{ yearFilter.year }} ({{ yearFilter.count }})
                      </a>
                    </div>
                  </div>
                  <div
                    v-for="monthFilter in visibleMonthFilters(yearFilter.year)"
                    :key="`${yearFilter.year}-${monthFilter.month}`"
                    class="row g-0 filter-row home-month-filter-row"
                  >
                    <div class="col g-0">
                      <a
                        :class="{ active: selectedWatchedMonth === monthFilter.month }"
                        :href="homeWatchedFilterHref(homeContentPath(currentContentType, 'watched', yearFilter.year, monthFilter.month))"
                        :data-testid="`home-month-filter-${monthFilter.month}`"
                        @click.prevent="setWatchedMonthFilter(monthFilter.month)"
                      >
                        {{ monthFilter.label }} ({{ monthFilter.count }})
                      </a>
                    </div>
                  </div>
                </template>
              </template>
              <div
                v-if="ratingFiltersVisible"
                class="row g-0 single-filter-title-row"
              >
                <div class="col g-0">
                  <h3>By rating</h3>
                </div>
              </div>
              <div
                v-if="ratingFiltersVisible"
                class="row g-0 filter-row"
              >
                <div class="col g-0">
                  <a
                    :href="homeContentPath(currentContentType, 'rated')"
                    :class="{ active: selectedRatedRating === '' }"
                    data-testid="home-rating-filter-all"
                    @click.prevent="setRatedRatingFilter('')"
                  >
                    All
                  </a>
                </div>
              </div>
              <template v-if="ratingFiltersVisible">
                <div
                  v-for="ratingFilter in ratedRatingFilters"
                  :key="ratingFilter.rating"
                  class="row g-0 filter-row"
                >
                  <div class="col g-0">
                    <a
                      :href="homeContentPath(currentContentType, 'rated', ratingFilter.rating)"
                      :class="{ active: selectedRatedRating === String(ratingFilter.rating) }"
                      :data-testid="`home-rating-filter-${ratingFilter.rating}`"
                      @click.prevent="setRatedRatingFilter(String(ratingFilter.rating))"
                    >
                      {{ ratingFilter.rating }} ({{ ratingFilter.count }})
                    </a>
                  </div>
                </div>
              </template>
            </div>
          </aside>
        </div>
      </div>
    </section>

    <MobileSearchOverlay :open="mobileSearchOpen" @close="closeMobileSearchOverlay" />
    <div
      v-if="homeFiltersModalOpen"
      class="person-compact-rating-picker-overlay home-filters-modal-overlay"
      data-testid="home-filters-modal-overlay"
      @click.self="closeHomeFiltersModal"
    >
      <div
        class="home-filters-modal"
        data-testid="home-filters-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="home-filters-modal-title"
      >
        <button
          class="person-compact-rating-picker__close"
          data-testid="home-filters-modal-close"
          type="button"
          aria-label="Close filters"
          @click="closeHomeFiltersModal"
        >
          <span class="person-compact-rating-picker__close-icon ion-close" aria-hidden="true"></span>
        </button>
        <div class="container person-filters home-filters-modal__filters">
          <div
            class="home-filters-modal__header"
            data-testid="home-filters-modal-header"
          >
            <h2 id="home-filters-modal-title" data-testid="home-filters-modal-title">Filters:</h2>
            <button
              class="sorting-btn filter-btn"
              data-testid="home-filters-modal-apply"
              type="button"
              @click="closeHomeFiltersModal"
            >
              Apply
            </button>
          </div>
          <div
            class="row g-0 filters-groups-row"
            data-testid="home-filters-modal-groups"
          >
            <div
              v-if="sidebarPrimaryFilters.length"
              class="col-6 home-filters-modal__group"
              data-testid="home-filters-modal-group"
            >
              <div v-if="sidebarPrimaryFilters.length" class="row g-0 single-filter-title-row">
                <div class="col g-0">
                  <h3>{{ currentContentType === 'lists' ? 'Type' : 'Status' }}</h3>
                </div>
              </div>
              <div
                v-for="statusFilter in sidebarPrimaryFilters"
                :key="`modal-${statusFilter.key}`"
                :class="[
                  'row',
                  'g-0',
                  'filter-row',
                  { 'home-status-filter-row--separated': statusFilter.separated },
                ]"
              >
                <div class="col g-0">
                  <a
                    :href="statusFilter.href"
                    :class="{ active: statusFilter.active }"
                    @click.prevent="statusFilter.onClick(false)"
                  >
                    {{ statusFilter.label }}
                  </a>
                </div>
              </div>
              <template v-if="watchedFormatInPrimaryFiltersVisible">
                <div class="row g-0 single-filter-title-row">
                  <div class="col g-0">
                    <h3>By format</h3>
                  </div>
                </div>
                <div
                  v-for="formatFilter in watchedFormatFilters"
                  :key="`modal-primary-format-${formatFilter.format || 'all'}`"
                  class="row g-0 filter-row"
                >
                  <div class="col g-0">
                    <a
                      :href="homeWatchedFormatFilterHref(formatFilter.format)"
                      :class="{ active: selectedWatchedFormat === formatFilter.format }"
                      @click.prevent="setWatchedFormatFilter(formatFilter.format, false)"
                    >
                      {{ watchedFormatFilterLabel(formatFilter) }}
                    </a>
                  </div>
                </div>
              </template>
            </div>
            <div
              v-if="watchedFormatStandaloneFiltersVisible"
              class="col-6 home-filters-modal__group"
              data-testid="home-filters-modal-group"
            >
              <div class="row g-0 single-filter-title-row">
                <div class="col g-0">
                  <h3>By format</h3>
                </div>
              </div>
              <div
                v-for="formatFilter in watchedFormatFilters"
                :key="`modal-format-${formatFilter.format || 'all'}`"
                class="row g-0 filter-row"
              >
                <div class="col g-0">
                  <a
                    :href="homeWatchedFormatFilterHref(formatFilter.format)"
                    :class="{ active: selectedWatchedFormat === formatFilter.format }"
                    @click.prevent="setWatchedFormatFilter(formatFilter.format, false)"
                  >
                    {{ watchedFormatFilterLabel(formatFilter) }}
                  </a>
                </div>
              </div>
            </div>
            <div
              v-if="watchedYearFiltersVisible"
              :class="[
                'home-filters-modal__group',
                watchedFormatStandaloneFiltersVisible ? 'home-filters-modal__group--full' : 'col-6',
              ]"
              data-testid="home-filters-modal-group"
            >
              <div class="row g-0 single-filter-title-row">
                <div class="col g-0">
                  <h3>By year</h3>
                </div>
              </div>
              <div class="row g-0 filter-row">
                <div class="col g-0">
                  <a
                    :href="homeWatchedFilterHref(homeContentPath(currentContentType, 'watched'))"
                    :class="{ active: selectedWatchedYear === '' }"
                    @click.prevent="setWatchedYearFilter('', false)"
                  >
                    All
                  </a>
                </div>
              </div>
              <template
                v-for="yearFilter in watchedYearFilters"
                :key="`modal-year-${yearFilter.year}`"
              >
                <div class="row g-0 filter-row">
                  <div class="col g-0">
                    <a
                      :class="{ active: selectedWatchedYear === yearFilter.year }"
                      :href="homeWatchedFilterHref(homeContentPath(currentContentType, 'watched', yearFilter.year))"
                      @click.prevent="setWatchedYearFilter(yearFilter.year, false)"
                    >
                      {{ yearFilter.year }} ({{ yearFilter.count }})
                    </a>
                  </div>
                </div>
                <div
                  v-for="monthFilter in visibleMonthFilters(yearFilter.year)"
                  :key="`modal-${yearFilter.year}-${monthFilter.month}`"
                  class="row g-0 filter-row home-month-filter-row"
                >
                  <div class="col g-0">
                    <a
                      :class="{ active: selectedWatchedMonth === monthFilter.month }"
                      :href="homeWatchedFilterHref(homeContentPath(currentContentType, 'watched', yearFilter.year, monthFilter.month))"
                      @click.prevent="setWatchedMonthFilter(monthFilter.month, false)"
                    >
                      {{ monthFilter.label }} ({{ monthFilter.count }})
                    </a>
                  </div>
                </div>
              </template>
            </div>
            <div
              v-if="ratingFiltersVisible"
              class="col-6 home-filters-modal__group"
              data-testid="home-filters-modal-group"
            >
              <div class="row g-0 single-filter-title-row">
                <div class="col g-0">
                  <h3>By rating</h3>
                </div>
              </div>
              <div class="row g-0 filter-row">
                <div class="col g-0">
                  <a
                    :href="homeContentPath(currentContentType, 'rated')"
                    :class="{ active: selectedRatedRating === '' }"
                    data-testid="home-modal-rating-filter-all"
                    @click.prevent="setRatedRatingFilter('', false)"
                  >
                    All
                  </a>
                </div>
              </div>
              <template
                v-for="ratingFilter in ratedRatingFilters"
                :key="`modal-rating-${ratingFilter.rating}`"
              >
                <div class="row g-0 filter-row">
                  <div class="col g-0">
                    <a
                      :href="homeContentPath(currentContentType, 'rated', ratingFilter.rating)"
                      :class="{ active: selectedRatedRating === String(ratingFilter.rating) }"
                      :data-testid="`home-modal-rating-filter-${ratingFilter.rating}`"
                      @click.prevent="setRatedRatingFilter(String(ratingFilter.rating), false)"
                    >
                      {{ ratingFilter.rating }} ({{ ratingFilter.count }})
                    </a>
                  </div>
                </div>
              </template>
            </div>
            <div
              v-if="showFiltersVisible"
              :class="[
                'home-filters-modal__group',
                ratingFiltersVisible || watchedYearFiltersVisible ? 'home-filters-modal__group--full' : 'col-6',
              ]"
              data-testid="home-filters-modal-group"
            >
              <div class="row g-0 single-filter-title-row">
                <div class="col g-0">
                  <h3>By show</h3>
                </div>
              </div>
              <div class="row g-0 filter-row">
                <div class="col g-0">
                  <a
                    href="#"
                    :class="{ active: selectedWatchedShow === '' }"
                    @click.prevent="setWatchedShowFilter('', false)"
                  >
                    All
                  </a>
                </div>
              </div>
              <template
                v-for="showFilter in watchedShowFilters"
                :key="`modal-show-${showFilter.show}`"
              >
                <div class="row g-0 filter-row">
                  <div class="col g-0">
                    <a
                      href="#"
                      :class="{ active: selectedWatchedShow === showFilter.show }"
                      @click.prevent="setWatchedShowFilter(showFilter.show, false)"
                    >
                      {{ showFilter.title }} ({{ showFilter.count }})
                    </a>
                  </div>
                </div>
              </template>
              <div
                v-if="watchedShowDropdownFilters.length"
                class="row g-0 filter-row"
              >
                <div class="col g-0">
                  <div class="home-show-filter-dropdown">
                    <button
                      class="home-show-filter-dropdown__toggle"
                      data-testid="home-show-filter-toggle"
                      type="button"
                      aria-haspopup="listbox"
                      :aria-expanded="watchedShowDropdownOpen ? 'true' : 'false'"
                      @click="toggleWatchedShowDropdown"
                    >
                      {{ selectedWatchedShowDropdownLabel }}
                    </button>
                    <div
                      v-if="watchedShowDropdownOpen"
                      class="home-show-filter-dropdown__menu"
                      data-testid="home-show-filter-menu"
                      role="listbox"
                    >
                      <input
                        v-model="watchedShowDropdownQuery"
                        class="home-show-filter-dropdown__search"
                        data-testid="home-show-filter-search"
                        type="text"
                        placeholder="Filter shows"
                        @keydown.escape="closeWatchedShowDropdown"
                      >
                      <button
                        class="home-show-filter-dropdown__option"
                        data-testid="home-show-filter-dropdown-all"
                        type="button"
                        role="option"
                        :aria-selected="selectedWatchedShow === '' ? 'true' : 'false'"
                        @click="selectWatchedShowFromDropdown('', false)"
                      >
                        {{ allWatchedShowsDropdownLabel }}
                      </button>
                      <button
                        v-for="showFilter in filteredWatchedShowDropdownFilters"
                        :key="`modal-dropdown-${showFilter.show}`"
                        class="home-show-filter-dropdown__option"
                        :class="{ active: selectedWatchedShow === showFilter.show }"
                        :data-testid="`home-show-filter-dropdown-${showFilter.show}`"
                        type="button"
                        role="option"
                        :aria-selected="selectedWatchedShow === showFilter.show ? 'true' : 'false'"
                        @click="selectWatchedShowFromDropdown(showFilter.show, false)"
                      >
                        {{ showFilter.title }} ({{ showFilter.count }})
                      </button>
                      <p
                        v-if="filteredWatchedShowDropdownFilters.length === 0"
                        class="home-show-filter-dropdown__empty"
                        data-testid="home-show-filter-empty"
                      >
                        No shows found
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="homeListModalOpen"
      class="person-compact-rating-picker-overlay"
      data-testid="home-list-modal-overlay"
      @click.self="closeHomeListModal"
    >
      <form
        class="home-list-modal film-watch-modal"
        data-testid="home-list-modal"
        @submit.prevent="submitHomeListCreate"
      >
        <button
          class="person-compact-rating-picker__close film-watch-modal__close"
          type="button"
          aria-label="Close list modal"
          @click="closeHomeListModal"
        >
          <span class="person-compact-rating-picker__close-icon ion-close" aria-hidden="true"></span>
        </button>
        <h2 class="film-watch-modal__title" data-testid="home-list-modal-title">
          Add new {{ homeListModalType.label.toLowerCase() }} list
        </h2>
        <p v-if="homeListModalError" class="home-list-modal__error" data-testid="home-list-modal-error">
          {{ homeListModalError }}
        </p>
        <label class="home-list-modal__field film-watch-modal__field">
          <span>Title</span>
          <input
            v-model.trim="homeListForm.title"
            class="film-list-modal__input"
            data-testid="home-list-modal-title-input"
            type="text"
            maxlength="255"
            required
          >
        </label>
        <label class="home-list-modal__field film-watch-modal__field">
          <span>Description</span>
          <textarea
            v-model.trim="homeListForm.description"
            class="film-watch-modal__textarea"
            data-testid="home-list-modal-description"
            rows="4"
          ></textarea>
        </label>
        <label class="home-list-modal__checkbox film-watch-modal__checkbox" data-testid="home-list-modal-public-field">
          <input
            v-model="homeListForm.public"
            data-testid="home-list-modal-public"
            type="checkbox"
          >
          <span>Public</span>
        </label>
        <div class="home-list-modal__actions film-watch-modal__actions" data-testid="home-list-modal-actions">
          <div class="film-watch-modal__action-group">
            <button
              class="film-watch-modal__action film-watch-modal__action--secondary"
              data-testid="home-list-modal-cancel"
              type="button"
              @click="closeHomeListModal"
            >
              Cancel
            </button>
            <button
              class="film-watch-modal__action"
              data-testid="home-list-modal-submit"
              type="submit"
              :disabled="homeListModalSaving"
            >
              {{ homeListModalSaving ? 'Adding...' : 'Add list' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </main>
</template>

<script setup>
import { usePageViewTracking } from '../analytics/pageViews'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import CompactMobilePagination from '../components/CompactMobilePagination.vue'
import ViewModeIcon from '../components/ViewModeIcon'
import { useAuthStore } from '../stores/auth'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import '../assets/single-person/person-detail.css'
import '../assets/home/styles.css'
import { buildDocumentTitle, setDocumentTitle } from '../utils/documentTitle'
import { buildMetaDescription, useMetaDescription } from '../utils/metaDescription'
import { compactPaginationItems } from '../utils/pagination'

const logoPath = '/static/spa/branding/logo.png'
const posterPlaceholderImage = '/static/films/img/placeholder-film-camera.svg'
const episodePlaceholderImage = '/static/films/img/placeholder-episode-tv.svg'
const route = useRoute()
const capturePageView = usePageViewTracking()
const router = useRouter()
const auth = useAuthStore()
let sessionExpiryRedirectPending = false
const listDeletedNoticeStorageKey = 'filmsomniac:listDeletedNotice'
const mobileSearchOpen = ref(false)
const publicProfileUsername = ref('')
const watchedFilms = ref([])
const watchedFilmsLoading = ref(false)
const watchedFilmsError = ref('')
const watchedFilmsPage = ref(1)
const watchedFilmsNumPages = ref(1)
const watchedFilmsCount = ref(0)
const watchedYearFilters = ref([])
const watchedMonthFilters = ref([])
const watchedFormatFilters = ref([])
const watchedShowFilters = ref([])
const watchedShowDropdownFilters = ref([])
const watchedShowDropdownOpen = ref(false)
const watchedShowDropdownQuery = ref('')
const ratedRatingFilters = ref([])
const watchCounts = ref({
  films: {
    uniqueWatched: 0,
    watches: 0,
  },
  episodes: {
    uniqueWatched: 0,
    watches: 0,
  },
})
const homeLists = ref([])
const homeListTypeFilters = ref([])
const homeListsLoading = ref(false)
const homeListsError = ref('')
const homeListsPage = ref(1)
const homeListsNumPages = ref(1)
const selectedHomeListTypeKey = ref('film')
const expandedHomeListDescriptionIds = ref({})
const expandedNextEpisodeOverviewIds = ref({})
const listDeletedNotice = ref('')
const homeFiltersModalOpen = ref(false)
const homeListModalOpen = ref(false)
const homeListModalSaving = ref(false)
const homeListModalError = ref('')
const homeListModalTypeKey = ref('film')
const homeListForm = ref({
  title: '',
  description: '',
  public: true,
})
const accountSettingsLoading = ref(false)
const accountSettingsError = ref('')
const accountEmail = ref('')
const accountEmailEditing = ref(false)
const accountEmailSaving = ref(false)
const accountEmailMessage = ref('')
const accountEmailError = ref('')
const accountPasswordSaving = ref(false)
const accountPasswordMessage = ref('')
const accountPasswordError = ref('')
const accountRemovalSaving = ref(false)
const accountRemovalError = ref('')
const accountRemovalRequestedAt = ref(null)
const accountRemovalReason = ref('')
const accountGoogleConnected = ref(false)
const accountGoogleMessage = ref('')
const accountGoogleError = ref('')
const accountGoogleSaving = ref(false)
const accountHasUsablePassword = ref(true)
const accountEmailForm = ref({
  newEmail: '',
  newEmailConfirm: '',
})
const accountPasswordForm = ref({
  currentPassword: '',
  newPassword: '',
  newPasswordConfirm: '',
})
const accountEmailSaveDisabled = computed(() => {
  const newEmail = accountEmailForm.value.newEmail
  const newEmailConfirm = accountEmailForm.value.newEmailConfirm
  return accountEmailSaving.value || !newEmail || newEmail !== newEmailConfirm
})
const accountPasswordSaveDisabled = computed(() => {
  const currentPassword = accountPasswordForm.value.currentPassword
  const newPassword = accountPasswordForm.value.newPassword
  const newPasswordConfirm = accountPasswordForm.value.newPasswordConfirm
  return (
    accountPasswordSaving.value
    || (accountHasUsablePassword.value && !currentPassword)
    || !isValidAccountPassword(newPassword)
    || newPassword !== newPasswordConfirm
  )
})
const selectedWatchedShow = ref('')
const selectedWatchedYear = ref('')
const selectedWatchedMonth = ref('')
const selectedWatchedFormat = ref('')
const selectedRatedRating = ref('')
const currentSortMode = ref('last-added')
const currentFilmStatus = ref('watched')
const homeViewMode = ref('2')
const homeViewModes = ['1', '2', '4']
const filmStatusFromPath = {
  watched: 'watched',
  rated: 'rated',
  favourites: 'favourited',
  watchlist: 'watchlisted',
  next: 'next',
}
const filmStatusPathSegment = {
  watched: 'watched',
  rated: 'rated',
  favourited: 'favourites',
  watchlisted: 'watchlist',
  next: 'next',
}
const defaultStatusByContentType = {
  films: 'watched',
  shows: 'rated',
  seasons: 'favourited',
  episodes: 'watched',
  people: 'favourited',
  lists: '',
}
const contentTabsConfig = [
  { key: 'films', label: 'Films' },
  { key: 'shows', label: 'Shows' },
  { key: 'seasons', label: 'Seasons' },
  { key: 'episodes', label: 'Episodes' },
  { key: 'people', label: 'People' },
  { key: 'lists', label: 'Lists' },
]
const publicContentTabsConfig = [
  { key: 'films', label: 'Films' },
  { key: 'episodes', label: 'Episodes' },
  { key: 'lists', label: 'Lists' },
]
const statusFiltersByContentType = {
  films: [
    { key: 'watched', label: 'Watched' },
    { key: 'rated', label: 'Rated' },
    { key: 'favourited', label: 'Favourited' },
    { key: 'watchlisted', label: 'Watchlisted' },
  ],
  shows: [
    { key: 'rated', label: 'Rated' },
    { key: 'favourited', label: 'Favourited' },
    { key: 'watchlisted', label: 'Watchlisted' },
  ],
  seasons: [],
  episodes: [
    { key: 'watched', label: 'Watched' },
    { key: 'rated', label: 'Rated' },
    { key: 'favourited', label: 'Favourited' },
    { key: 'next', label: 'Next episodes', separated: true },
  ],
  people: [{ key: 'favourited', label: 'Favourited' }],
}
const contentTypeLabel = {
  films: 'films',
  shows: 'shows',
  seasons: 'seasons',
  episodes: 'episodes',
  people: 'people',
  lists: 'lists',
}
const homeDocumentTitleLabels = {
  films: 'Films',
  shows: 'Shows',
  seasons: 'Seasons',
  episodes: 'Episodes',
  people: 'People',
  lists: 'Lists',
}
const homeListTypes = [
  { key: 'film', label: 'Film' },
  { key: 'show', label: 'Show' },
  { key: 'episode', label: 'Episode' },
  { key: 'people', label: 'People' },
]
const homeListSortOptions = new Set([
  'last-updated',
  'earliest-updated',
  'title-asc',
  'title-desc',
])
const watchedSortOptions = new Set([
  'first-added',
  'last-added',
  'watch-date-asc',
  'watch-date-desc',
  'title-asc',
  'title-desc',
  'oldest',
  'newest',
])
const episodeWatchedSortOptions = new Set([
  'first-added',
  'last-added',
  'watch-date-asc',
  'watch-date-desc',
])
const ratedSortOptions = new Set([
  'first-added',
  'last-added',
  'title-asc',
  'title-desc',
  'oldest',
  'newest',
])
const episodeRatedSortOptions = new Set([
  'first-added',
  'last-added',
])
const nextEpisodeSortOptions = new Set([
  'recently-watched',
  'show-title-asc',
  'show-title-desc',
  'air-date-desc',
  'air-date-asc',
])
const homeListItemCountLabels = {
  film: { singular: 'film', plural: 'films' },
  show: { singular: 'show', plural: 'shows' },
  episode: { singular: 'episode', plural: 'episodes' },
  people: { singular: 'person', plural: 'people' },
}
const homeListDescriptionDisplayLimit = 300
const nextEpisodeOverviewDisplayLimit = 150

const isPublicProfile = computed(() => route.name === 'public-profile')
const isDemoProfile = computed(() => route.name === 'demo-profile')
const isReadOnlyProfile = computed(() => isPublicProfile.value || isDemoProfile.value)
const publicUsername = computed(() => String(route.params.username || '').trim())
const publicProfileDisplayName = computed(() => (
  publicProfileUsername.value || publicUsername.value
))
const readOnlyProfileDisplayName = computed(() => (
  isDemoProfile.value ? (publicProfileUsername.value || 'Colourblind') : publicProfileDisplayName.value
))
const activeSection = computed(() => (route.path === '/home/settings' ? 'settings' : 'home'))
const isSettingsSection = computed(() => activeSection.value === 'settings')
const googleConnectErrorMessage = computed(() => (
  route.query.googleConnect === 'connectedOther'
    ? 'Another Filmsomniac account is already connected to that Google account.'
    : ''
))
const googleConnectSuccessMessage = computed(() => (
  route.query.googleConnect === 'connected'
    ? 'Google account connected.'
    : ''
))
const settingsPanel = computed(() => (
  route.query.panel === 'account-removal' ? 'account-removal' : 'account'
))
const settingsLinks = computed(() => [
  { key: 'account', label: 'Account', href: '/home/settings' },
  accountGoogleConnected.value
    ? { key: 'google-disconnect', label: 'Disconnect Google account', href: '/home/settings' }
    : {
        key: 'google-connect',
        label: 'Connect to Google account',
        href: '/api/v1/auth/google/login?process=connect&next=%2Fhome%2Fsettings%3FgoogleConnect%3Dconnected',
        external: true,
      },
  { key: 'account-removal', label: 'Account removal', href: '/home/settings?panel=account-removal' },
])
const currentContentType = computed(() => {
  const contentType = String(route.params.contentType || '')
  if (isPublicProfile.value) {
    return publicContentTabsConfig.some((contentTab) => contentTab.key === contentType)
      ? contentType
      : 'films'
  }
  if (contentTabsConfig.some((contentTab) => contentTab.key === contentType)) {
    return contentType
  }
  return route.params.medium === 'TV' ? 'episodes' : 'films'
})
const currentMedium = computed(() => (currentContentType.value === 'episodes' ? 'tv' : 'film'))
const tabs = computed(() => [
  { key: 'home', label: 'Home', href: mediumPath(), active: activeSection.value === 'home' },
  {
    key: 'settings',
    label: 'Settings',
    href: mediumPath('settings'),
    active: activeSection.value === 'settings',
  },
])
const activeContentTabsConfig = computed(() => (
  isPublicProfile.value ? publicContentTabsConfig : contentTabsConfig
))
const contentTabs = computed(() => activeContentTabsConfig.value.map((contentTab) => ({
  ...contentTab,
  href: homeContentPath(contentTab.key),
  active: currentContentType.value === contentTab.key,
})))
const statusFilters = computed(() => statusFiltersByContentType[currentContentType.value] || [])
const selectedHomeListType = computed(() => (
  homeListTypes.find((listType) => listType.key === selectedHomeListTypeKey.value) || homeListTypes[0]
))
const homeListModalType = computed(() => (
  homeListTypes.find((listType) => listType.key === homeListModalTypeKey.value) || homeListTypes[0]
))
const sidebarPrimaryFilters = computed(() => {
  if (currentContentType.value === 'lists') {
    return homeListTypes.map((listType) => {
      const filter = homeListTypeFilters.value.find((item) => item.key === listType.key)
      const count = Number(filter?.count || 0)
      return {
        key: listType.key,
        label: `${listType.label} (${count})`,
        href: homeListsPath(listType.key),
        active: selectedHomeListTypeKey.value === listType.key,
        testId: `home-list-type-${listType.key}`,
        onClick: (closeFiltersModal = true) => setHomeListType(listType.key, closeFiltersModal),
      }
    })
  }

  if (isPublicProfile.value) {
    return []
  }

  return statusFilters.value.map((statusFilter) => ({
    ...statusFilter,
    href: homeContentPath(currentContentType.value, statusFilter.key),
    active: currentFilmStatus.value === statusFilter.key,
    testId: `home-status-${statusFilter.key}`,
    onClick: (closeFiltersModal = true) => setFilmStatus(statusFilter.key, closeFiltersModal),
  }))
})
const sortButtons = computed(() => {
  if (currentContentType.value === 'lists') {
    return [
      { key: 'last-updated', label: 'Last updated', active: currentSortMode.value === 'last-updated' },
      { key: 'earliest-updated', label: 'Earliest updated', active: currentSortMode.value === 'earliest-updated' },
      { key: 'title-asc', label: 'Title A-Z', active: currentSortMode.value === 'title-asc' },
      { key: 'title-desc', label: 'Title Z-A', active: currentSortMode.value === 'title-desc' },
    ]
  }

  if (currentFilmStatus.value === 'next') {
    return [
      { key: 'recently-watched', label: 'Recently watched', active: currentSortMode.value === 'recently-watched' },
      { key: 'show-title-asc', label: 'Show title A-Z', active: currentSortMode.value === 'show-title-asc' },
      { key: 'show-title-desc', label: 'Show title Z-A', active: currentSortMode.value === 'show-title-desc' },
      { key: 'air-date-desc', label: 'Last air date', active: currentSortMode.value === 'air-date-desc' },
      { key: 'air-date-asc', label: 'Earliest air date', active: currentSortMode.value === 'air-date-asc' },
    ]
  }

  const titleSortLabel = currentContentType.value === 'people' ? 'Name' : 'Title'
  const showTitleSortButtons = currentContentType.value !== 'episodes'
  const showYearSortButtons = !['episodes', 'people'].includes(currentContentType.value)

  return [
    { key: 'first-added', label: 'First added', active: currentSortMode.value === 'first-added' },
    { key: 'last-added', label: 'Last added', active: currentSortMode.value === 'last-added' },
    ...(currentFilmStatus.value === 'watched'
      ? [
          { key: 'watch-date-asc', label: 'Watch date 0-9', active: currentSortMode.value === 'watch-date-asc' },
          { key: 'watch-date-desc', label: 'Watch date 9-0', active: currentSortMode.value === 'watch-date-desc' },
        ]
      : []),
    ...(showTitleSortButtons
      ? [
          { key: 'title-asc', label: `${titleSortLabel} A-Z`, active: currentSortMode.value === 'title-asc' },
          { key: 'title-desc', label: `${titleSortLabel} Z-A`, active: currentSortMode.value === 'title-desc' },
        ]
      : []),
    ...(showYearSortButtons
      ? [
          { key: 'oldest', label: 'Oldest', active: currentSortMode.value === 'oldest' },
          { key: 'newest', label: 'Newest', active: currentSortMode.value === 'newest' },
        ]
      : []),
  ]
})
const mobileSortButtonRows = computed(() => {
  const buttonsByKey = Object.fromEntries(sortButtons.value.map((button) => [button.key, button]))
  const rowKeys = currentContentType.value === 'lists'
    ? [
        ['last-updated', 'earliest-updated'],
        ['title-asc', 'title-desc'],
      ]
    : currentFilmStatus.value === 'next'
    ? [
        ['recently-watched'],
        ['show-title-asc', 'show-title-desc'],
        ['air-date-desc', 'air-date-asc'],
      ]
    : currentContentType.value === 'episodes' && currentFilmStatus.value === 'watched'
    ? [
        ['first-added', 'last-added', 'watch-date-asc', 'watch-date-desc'],
      ]
    : currentContentType.value === 'episodes'
    ? [
        ['first-added', 'last-added'],
      ]
    : currentFilmStatus.value === 'watched'
    ? [
        ['first-added', 'last-added', 'watch-date-asc', 'watch-date-desc'],
        ['title-asc', 'title-desc', 'oldest', 'newest'],
      ]
    : [
        ['first-added', 'last-added'],
        ['title-asc', 'title-desc', 'oldest', 'newest'],
      ]

  return rowKeys.map((row) => row.map((key) => buttonsByKey[key]).filter(Boolean))
})
const homeViewModeControlsVisible = computed(() => (
  ['films', 'shows', 'seasons', 'episodes', 'people'].includes(currentContentType.value) &&
  ['watched', 'rated', 'favourited', 'watchlisted', 'next'].includes(currentFilmStatus.value)
))
const homeViewModeButtons = computed(() => ([
  { key: '1', label: 'Detailed view', active: homeViewMode.value === '1' },
  { key: '2', label: 'Poster grid view', active: homeViewMode.value === '2' },
  { key: '4', label: 'Text list view', active: homeViewMode.value === '4' },
]))
const homeDetailsViewVisible = computed(() => (
  homeViewModeControlsVisible.value && homeViewMode.value === '1'
))
const homeTextOnlyViewVisible = computed(() => (
  homeViewModeControlsVisible.value && homeViewMode.value === '4'
))
const filmStatusHeading = computed(() => {
  if (currentContentType.value === 'lists') {
    return `${selectedHomeListType.value.label} lists`
  }

  if (currentContentType.value === 'episodes') {
    if (currentFilmStatus.value === 'next') {
      return 'Next episodes'
    }

    const episodeStatusLabel = {
      favourited: 'Favourite',
      rated: 'Rated',
    }[currentFilmStatus.value]
    if (episodeStatusLabel) {
      return `${episodeStatusLabel} episodes (${watchedFilmsCount.value})`
    }

    return 'Watched episodes'
  }

  if (currentContentType.value === 'films') {
    const filmStatusLabel = {
      favourited: 'Favourite',
      rated: 'Rated',
      watchlisted: 'Watchlisted',
    }[currentFilmStatus.value]
    if (filmStatusLabel) {
      return `${filmStatusLabel} films (${watchedFilmsCount.value})`
    }
  }

  if (currentContentType.value === 'shows') {
    const showStatusLabel = {
      favourited: 'Favourite',
      rated: 'Rated',
      watchlisted: 'Watchlisted',
    }[currentFilmStatus.value]
    if (showStatusLabel) {
      return `${showStatusLabel} shows (${watchedFilmsCount.value})`
    }
  }

  if (
    currentContentType.value === 'seasons' &&
    currentFilmStatus.value === 'favourited'
  ) {
    return `Favourite seasons (${watchedFilmsCount.value})`
  }

  if (
    currentContentType.value === 'people' &&
    currentFilmStatus.value === 'favourited'
  ) {
    return `Favourite people (${watchedFilmsCount.value})`
  }

  const statusLabel = {
    favourited: 'Favourited',
    rated: 'Rated',
    watched: 'Watched',
    watchlisted: 'Watchlisted',
  }[currentFilmStatus.value] || 'Watched'
  return `${statusLabel} ${contentTypeLabel[currentContentType.value] || 'films'}`
})
const filmStatusHasSortControls = computed(() => (
  currentContentType.value === 'lists' ||
  ['watched', 'rated', 'favourited', 'watchlisted', 'next'].includes(currentFilmStatus.value)
))
const ratingFiltersVisible = computed(() => (
  !isPublicProfile.value &&
  currentFilmStatus.value === 'rated' &&
  ['films', 'shows', 'episodes'].includes(currentContentType.value)
))
const showFiltersVisible = computed(() => (
  currentContentType.value === 'episodes' &&
  (isPublicProfile.value
    ? currentFilmStatus.value === 'watched'
    : ['watched', 'rated', 'favourited'].includes(currentFilmStatus.value))
))
const watchedYearFiltersVisible = computed(() => (
  currentFilmStatus.value === 'watched' &&
  (isPublicProfile.value
    ? currentContentType.value === 'films'
    : ['films', 'episodes'].includes(currentContentType.value))
))
const watchedFormatFiltersVisible = computed(() => (
  !isPublicProfile.value &&
  ['films', 'episodes'].includes(currentContentType.value) &&
  currentFilmStatus.value === 'watched' &&
  watchedFormatFilters.value.length > 0
))
const watchedFormatInPrimaryFiltersVisible = computed(() => (
  watchedFormatFiltersVisible.value &&
  currentContentType.value === 'episodes'
))
const watchedFormatStandaloneFiltersVisible = computed(() => (
  watchedFormatFiltersVisible.value &&
  currentContentType.value === 'films'
))
const filtersPanelVisible = computed(() => (
  currentContentType.value !== 'people' &&
  (
    sidebarPrimaryFilters.value.length > 0 ||
    ratingFiltersVisible.value ||
    showFiltersVisible.value ||
    watchedFormatFiltersVisible.value ||
    watchedYearFiltersVisible.value
  )
))
const homeFilterButtonVisible = computed(() => (
  filtersPanelVisible.value
))
const homeViewModeFilterButtonVisible = computed(() => (
  homeFilterButtonVisible.value && ['films', 'shows', 'episodes'].includes(currentContentType.value)
))
const homeStatusTestPrefix = computed(() => ({
  favourited: 'favourited',
  next: 'next',
  rated: 'rated',
  watched: 'watched',
  watchlisted: 'watchlisted',
}[currentFilmStatus.value] || 'watched'))

function homeLoadErrorMessage() {
  return `Unable to load ${contentTypeLabel[currentContentType.value] || 'items'}.`
}

function homeListDescriptionKey(listItem) {
  return `${listItem?.type || ''}-${listItem?.id || ''}`
}

function isHomeListDescriptionExpanded(listItem) {
  const key = homeListDescriptionKey(listItem)
  return Boolean(key && expandedHomeListDescriptionIds.value[key])
}

function hasExpandableHomeListDescription(listItem) {
  return (
    String(listItem?.description || '').trim().length > homeListDescriptionDisplayLimit
  )
}

function displayedHomeListDescription(listItem) {
  const description = String(listItem?.description || '').trim()
  if (!hasExpandableHomeListDescription(listItem) || isHomeListDescriptionExpanded(listItem)) {
    return description
  }
  return `${description.slice(0, homeListDescriptionDisplayLimit).trim()}...`
}

function toggleHomeListDescription(listItem) {
  const key = homeListDescriptionKey(listItem)
  if (!key) {
    return
  }

  expandedHomeListDescriptionIds.value = {
    ...expandedHomeListDescriptionIds.value,
    [key]: !expandedHomeListDescriptionIds.value[key],
  }
}

function nextEpisodeOverviewKey(item) {
  return String(item?.next_episode_id || item?.id || '')
}

function isNextEpisodeOverviewExpanded(item) {
  const key = nextEpisodeOverviewKey(item)
  return Boolean(key && expandedNextEpisodeOverviewIds.value[key])
}

function hasExpandableNextEpisodeOverview(item) {
  return (
    String(item?.overview || '').trim().length > nextEpisodeOverviewDisplayLimit
  )
}

function displayedNextEpisodeOverview(item) {
  const overview = String(item?.overview || '').trim()
  if (!hasExpandableNextEpisodeOverview(item) || isNextEpisodeOverviewExpanded(item)) {
    return overview
  }
  return `${overview.slice(0, nextEpisodeOverviewDisplayLimit).trim()}...`
}

function toggleNextEpisodeOverview(item) {
  const key = nextEpisodeOverviewKey(item)
  if (!key) {
    return
  }

  expandedNextEpisodeOverviewIds.value = {
    ...expandedNextEpisodeOverviewIds.value,
    [key]: !expandedNextEpisodeOverviewIds.value[key],
  }
}

const canGoToPreviousWatchedFilmsPage = computed(() => watchedFilmsPage.value > 1)
const canGoToNextWatchedFilmsPage = computed(
  () => watchedFilmsPage.value < watchedFilmsNumPages.value,
)
const canGoToPreviousHomeListsPage = computed(() => homeListsPage.value > 1)
const canGoToNextHomeListsPage = computed(
  () => homeListsPage.value < homeListsNumPages.value,
)
const watchedFilmsPages = computed(() => (
  compactPaginationItems(watchedFilmsPage.value, watchedFilmsNumPages.value)
))
const homeListsPages = computed(() => (
  compactPaginationItems(homeListsPage.value, homeListsNumPages.value)
))
const selectedWatchedShowDropdownLabel = computed(() => {
  const selectedShow = [
    ...watchedShowFilters.value,
    ...watchedShowDropdownFilters.value,
  ].find((showFilter) => showFilter.show === selectedWatchedShow.value)

  return selectedShow ? `${selectedShow.title} (${selectedShow.count})` : allWatchedShowsDropdownLabel.value
})
const allWatchedShowsDropdownLabel = computed(() => {
  const showCount = watchedShowDropdownFilters.value.length
  return showCount ? `All shows (${showCount})` : 'All shows'
})
const filteredWatchedShowDropdownFilters = computed(() => {
  const query = watchedShowDropdownQuery.value.trim().toLowerCase()
  if (!query) {
    return watchedShowDropdownFilters.value
  }

  return watchedShowDropdownFilters.value.filter((showFilter) => (
    `${showFilter.title} ${showFilter.count}`.toLowerCase().includes(query)
  ))
})

function authHeaders() {
  return auth.state.access
    ? { Authorization: `Bearer ${auth.state.access}` }
    : {}
}

function homeRequestHeaders() {
  return isReadOnlyProfile.value
    ? { Accept: 'application/json' }
    : {
        Accept: 'application/json',
        ...authHeaders(),
      }
}

function redirectToLoginAfterUnauthorized(response) {
  if (
    response?.status !== 401 ||
    isReadOnlyProfile.value
  ) {
    return false
  }

  if (!sessionExpiryRedirectPending) {
    sessionExpiryRedirectPending = true
    auth.clearTokens()
    router.push({
      path: '/login',
      query: { redirect: route.fullPath || '/home', auth_redirect: '1' },
    })
  }
  return true
}

function redirectToNotFoundAfterMissingPublicProfile(response) {
  if (response?.status !== 404 || !isPublicProfile.value) {
    return false
  }

  router.replace({
    name: 'not-found',
    params: {
      pathMatch: route.path.replace(/^\/+/, '').split('/'),
    },
  })
  return true
}

async function loadPublicProfile() {
  if (isDemoProfile.value) {
    setDocumentTitle(buildDocumentTitle(['Demo Account', homeDocumentTitleLabels[currentContentType.value]]))
    publicProfileUsername.value = 'Colourblind'
    return
  }

  if (!isPublicProfile.value || !publicUsername.value) {
    publicProfileUsername.value = ''
    return
  }

  publicProfileUsername.value = publicUsername.value
  setDocumentTitle(buildDocumentTitle([publicProfileDisplayName.value]))
}

function applyPublicProfilePayload(payload) {
  if (!isPublicProfile.value) {
    return
  }
  publicProfileUsername.value = payload?.username || publicUsername.value
  setDocumentTitle(buildDocumentTitle([publicProfileDisplayName.value]))
}

function updateHomeDocumentTitle() {
  if (isReadOnlyProfile.value) {
    return
  }
  if (route.path === '/home' || isSettingsSection.value) {
    setDocumentTitle(buildDocumentTitle(['Home']))
    return
  }
  setDocumentTitle(buildDocumentTitle(['Home', homeDocumentTitleLabels[currentContentType.value]]))
}

async function loadWatchedFilms(page = 1) {
  const confirmPageView = capturePageView()
  if (currentContentType.value === 'lists') {
    watchedFilms.value = []
    watchedFilmsPage.value = 1
    watchedFilmsNumPages.value = 1
    watchedFilmsCount.value = 0
    watchedYearFilters.value = []
    watchedMonthFilters.value = []
    watchedFormatFilters.value = []
    watchedShowFilters.value = []
    watchedShowDropdownFilters.value = []
    ratedRatingFilters.value = []
    watchedFilmsError.value = ''
    watchedFilmsLoading.value = false
    await loadHomeLists(page)
    return
  }

  watchedFilmsLoading.value = true
  watchedFilmsError.value = ''
  try {
    const response = await fetch(watchedFilmsUrl(page), {
      headers: homeRequestHeaders(),
    })
    if (!response.ok) {
      if (redirectToNotFoundAfterMissingPublicProfile(response)) {
        return
      }
      if (redirectToLoginAfterUnauthorized(response)) {
        return
      }
      watchedFilmsError.value = homeLoadErrorMessage()
      watchedFilms.value = []
      watchedFilmsPage.value = 1
      watchedFilmsNumPages.value = 1
      watchedFilmsCount.value = 0
      watchedYearFilters.value = []
      watchedMonthFilters.value = []
      watchedFormatFilters.value = []
      watchedShowFilters.value = []
      watchedShowDropdownFilters.value = []
      ratedRatingFilters.value = []
      return
    }
    const payload = await response.json()
    confirmPageView()
    applyPublicProfilePayload(payload)
    watchedFilms.value = Array.isArray(payload.results) ? payload.results : []
    if (payload.watch_counts) {
      watchCounts.value = normalizeWatchCounts(payload.watch_counts)
    }
    expandedNextEpisodeOverviewIds.value = {}
    watchedFilmsPage.value = Number(payload.page) || page
    watchedFilmsNumPages.value = Number(payload.num_pages) || 1
    watchedFilmsCount.value = Number(payload.count) || 0
    currentSortMode.value = typeof payload.sort === 'string' ? payload.sort : currentSortMode.value
    const payloadYear = typeof payload.year === 'string' ? payload.year : selectedWatchedYear.value
    const payloadMonth = typeof payload.month === 'string' ? payload.month : selectedWatchedMonth.value
    const payloadFormat = typeof payload.format === 'string' ? payload.format : selectedWatchedFormat.value
    selectedWatchedShow.value = typeof payload.show === 'string' ? payload.show : selectedWatchedShow.value
    const nextYearFilters = Array.isArray(payload.year_filters) ? payload.year_filters : []
    const nextFormatFilters = Array.isArray(payload.format_filters) ? payload.format_filters : []
    if (
      watchedFormatFiltersVisible.value &&
      payloadFormat &&
      !nextFormatFilters.some((formatFilter) => String(formatFilter.format) === payloadFormat)
    ) {
      selectedWatchedFormat.value = ''
      watchedFormatFilters.value = nextFormatFilters
      router.replace({
        path: route.path,
        query: routeQueryWithWatchedFormat('', 1),
      })
      return
    }
    if (
      watchedYearFiltersVisible.value &&
      payloadYear &&
      !nextYearFilters.some((yearFilter) => String(yearFilter.year) === payloadYear)
    ) {
      selectedWatchedYear.value = ''
      selectedWatchedMonth.value = ''
      watchedYearFilters.value = nextYearFilters
      watchedMonthFilters.value = []
      router.replace(homeWatchedFilterLocation(homeContentPath(currentContentType.value, 'watched')))
      return
    }
    selectedWatchedYear.value = payloadYear
    selectedWatchedMonth.value = payloadMonth
    selectedWatchedFormat.value = payloadFormat
    watchedYearFilters.value = nextYearFilters
    watchedMonthFilters.value = Array.isArray(payload.month_filters) ? payload.month_filters : []
    watchedFormatFilters.value = nextFormatFilters
    watchedShowFilters.value = Array.isArray(payload.show_filters) ? payload.show_filters : []
    watchedShowDropdownFilters.value = Array.isArray(payload.show_dropdown_filters)
      ? payload.show_dropdown_filters
      : []
    selectedRatedRating.value = typeof payload.rating === 'string' ? payload.rating : selectedRatedRating.value
    ratedRatingFilters.value = Array.isArray(payload.rating_filters) ? payload.rating_filters : []
  } catch {
    watchedFilmsError.value = homeLoadErrorMessage()
    watchedFilms.value = []
    watchedFilmsPage.value = 1
    watchedFilmsNumPages.value = 1
    watchedFilmsCount.value = 0
    watchedYearFilters.value = []
    watchedMonthFilters.value = []
    watchedFormatFilters.value = []
    watchedShowFilters.value = []
    watchedShowDropdownFilters.value = []
    ratedRatingFilters.value = []
  } finally {
    watchedFilmsLoading.value = false
  }
}

async function loadWatchCounts() {
  const confirmPageView = capturePageView()
  if (isPublicProfile.value && !publicUsername.value) {
    return
  }
  if (isReadOnlyProfile.value) {
    return
  }

  try {
    const watchCountsUrl = isDemoProfile.value
      ? '/api/v1/home/demo/watch-counts'
      : isPublicProfile.value
        ? `/api/v1/home/public/${encodeURIComponent(publicUsername.value)}/watch-counts`
      : '/api/v1/home/watch-counts'
    const response = await fetch(watchCountsUrl, {
      headers: {
        Accept: 'application/json',
        ...(isReadOnlyProfile.value ? {} : authHeaders()),
      },
    })
    if (!response.ok) {
      if (redirectToNotFoundAfterMissingPublicProfile(response)) {
        return
      }
      redirectToLoginAfterUnauthorized(response)
      return
    }
    const payload = await response.json()
    confirmPageView()
    watchCounts.value = normalizeWatchCounts(payload)
  } catch {
    // Keep the default zero summary if the lightweight profile count request fails.
  }
}

function normalizeWatchCounts(payload) {
  return {
    films: {
      uniqueWatched: Number(payload?.films?.unique_watched) || 0,
      watches: Number(payload?.films?.watches) || 0,
    },
    episodes: {
      uniqueWatched: Number(payload?.episodes?.unique_watched) || 0,
      watches: Number(payload?.episodes?.watches) || 0,
    },
  }
}

async function loadHomeLists(page = 1) {
  const confirmPageView = capturePageView()
  homeListsLoading.value = true
  homeListsError.value = ''
  const params = new URLSearchParams({ type: selectedHomeListTypeKey.value })
  if (currentSortMode.value !== 'last-updated') {
    params.set('sort', currentSortMode.value)
  }
  if (page > 1) {
    params.set('page', String(page))
  }
  const listUrl = isPublicProfile.value
    ? `/api/v1/home/public/${encodeURIComponent(publicUsername.value)}/lists?${params.toString()}`
    : isDemoProfile.value
    ? `/api/v1/home/demo/lists?${params.toString()}`
    : `/api/v1/home/lists?${params.toString()}`
  try {
    const response = await fetch(listUrl, { headers: homeRequestHeaders() })
    if (!response.ok) {
      if (redirectToNotFoundAfterMissingPublicProfile(response)) {
        return
      }
      if (redirectToLoginAfterUnauthorized(response)) {
        return
      }
      homeLists.value = []
      homeListsPage.value = 1
      homeListsNumPages.value = 1
      homeListsError.value = 'Unable to load lists.'
      return
    }
    const payload = await response.json()
    confirmPageView()
    applyPublicProfilePayload(payload)
    if (payload.watch_counts) {
      watchCounts.value = normalizeWatchCounts(payload.watch_counts)
    }
    selectedHomeListTypeKey.value = typeof payload.selected_type === 'string'
      ? payload.selected_type
      : selectedHomeListTypeKey.value
    homeListTypeFilters.value = Array.isArray(payload.type_filters) ? payload.type_filters : []
    homeLists.value = Array.isArray(payload.results) ? payload.results : []
    expandedHomeListDescriptionIds.value = {}
    currentSortMode.value = typeof payload.sort === 'string' ? payload.sort : currentSortMode.value
    homeListsPage.value = Number(payload.page) || page
    homeListsNumPages.value = Number(payload.num_pages) || 1
  } catch {
    homeLists.value = []
    homeListsPage.value = 1
    homeListsNumPages.value = 1
    homeListsError.value = 'Unable to load lists.'
  } finally {
    homeListsLoading.value = false
  }
}

async function loadAccountSettings() {
  const confirmPageView = capturePageView()
  accountSettingsLoading.value = true
  accountSettingsError.value = ''
  try {
    const response = await fetch('/api/v1/auth/account', {
      headers: {
        Accept: 'application/json',
        ...authHeaders(),
      },
    })
    if (!response.ok) {
      if (redirectToLoginAfterUnauthorized(response)) {
        return
      }
      accountSettingsError.value = 'Unable to load account settings.'
      return
    }
    const payload = await response.json()
    confirmPageView()
    accountEmail.value = payload.email || ''
    accountGoogleConnected.value = Boolean(payload.google_account_connected)
    accountHasUsablePassword.value = payload.has_usable_password !== false
    accountGoogleMessage.value = googleConnectSuccessMessage.value
    accountGoogleError.value = googleConnectErrorMessage.value
    cancelEmailEdit()
    accountRemovalRequestedAt.value = payload.account_removal_requested_at || null
    accountRemovalReason.value = payload.account_removal_reason || ''
  } catch {
    accountSettingsError.value = 'Unable to load account settings.'
  } finally {
    accountSettingsLoading.value = false
  }
}

async function disconnectGoogleAccount() {
  if (accountGoogleSaving.value) {
    return
  }
  if (!window.confirm('Disconnect Google account?')) {
    return
  }
  accountGoogleSaving.value = true
  accountGoogleMessage.value = ''
  accountGoogleError.value = ''
  try {
    const response = await fetch('/api/v1/auth/google/connection', {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...authHeaders(),
      },
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      if (redirectToLoginAfterUnauthorized(response)) {
        return
      }
      accountGoogleError.value = accountApiError(payload, 'Unable to disconnect Google account.')
      return
    }
    accountGoogleConnected.value = Boolean(payload.google_account_connected)
    accountGoogleMessage.value = 'Google account disconnected.'
  } catch {
    accountGoogleError.value = 'Unable to disconnect Google account.'
  } finally {
    accountGoogleSaving.value = false
  }
}

function accountApiError(payload, fallback) {
  if (!payload || typeof payload !== 'object') {
    return fallback
  }
  const firstValue = Object.values(payload)[0]
  if (Array.isArray(firstValue)) {
    return String(firstValue[0] || fallback)
  }
  if (typeof firstValue === 'string') {
    return firstValue
  }
  return fallback
}

function startEmailEdit() {
  accountEmailEditing.value = true
  accountEmailMessage.value = ''
  accountEmailError.value = ''
  accountEmailForm.value = {
    newEmail: '',
    newEmailConfirm: '',
  }
}

function cancelEmailEdit() {
  accountEmailEditing.value = false
  accountEmailForm.value = {
    newEmail: '',
    newEmailConfirm: '',
  }
}

function isValidAccountPassword(password) {
  return password.length >= 10 && /\d/.test(password) && /[^A-Za-z0-9]/.test(password)
}

async function submitEmailChange() {
  if (accountEmailSaveDisabled.value) {
    return
  }
  accountEmailSaving.value = true
  accountEmailMessage.value = ''
  accountEmailError.value = ''
  try {
    const response = await fetch('/api/v1/auth/account', {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      body: JSON.stringify({ email: accountEmailForm.value.newEmail }),
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      if (redirectToLoginAfterUnauthorized(response)) {
        return
      }
      accountEmailError.value = accountApiError(payload, 'Unable to update email address.')
      return
    }
    accountEmail.value = payload.email || accountEmailForm.value.newEmail
    cancelEmailEdit()
    accountEmailMessage.value = 'Email address updated.'
  } catch {
    accountEmailError.value = 'Unable to update email address.'
  } finally {
    accountEmailSaving.value = false
  }
}

async function submitPasswordChange() {
  if (accountPasswordSaveDisabled.value) {
    return
  }
  accountPasswordSaving.value = true
  accountPasswordMessage.value = ''
  accountPasswordError.value = ''
  try {
    const body = {
      new_password: accountPasswordForm.value.newPassword,
      new_password_confirm: accountPasswordForm.value.newPasswordConfirm,
    }
    if (accountHasUsablePassword.value) {
      body.current_password = accountPasswordForm.value.currentPassword
    }
    const response = await fetch('/api/v1/auth/account/password', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      body: JSON.stringify(body),
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      if (redirectToLoginAfterUnauthorized(response)) {
        return
      }
      accountPasswordError.value = accountApiError(payload, 'Unable to change password.')
      return
    }
    accountPasswordForm.value = {
      currentPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
    }
    auth.clearTokens()
    await router.push({
      path: '/login',
      query: { passwordChanged: '1' },
    })
  } catch {
    accountPasswordError.value = 'Unable to change password.'
  } finally {
    accountPasswordSaving.value = false
  }
}

async function submitAccountRemovalRequest() {
  if (!window.confirm('Request account removal?')) {
    return
  }
  accountRemovalSaving.value = true
  accountRemovalError.value = ''
  try {
    const response = await fetch('/api/v1/auth/account/removal-request', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      body: JSON.stringify({ reason: accountRemovalReason.value }),
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      if (redirectToLoginAfterUnauthorized(response)) {
        return
      }
      accountRemovalError.value = accountApiError(payload, 'Unable to request account removal.')
      return
    }
    accountRemovalRequestedAt.value = payload.account_removal_requested_at || new Date().toISOString()
    accountRemovalReason.value = payload.account_removal_reason || accountRemovalReason.value
  } catch {
    accountRemovalError.value = 'Unable to request account removal.'
  } finally {
    accountRemovalSaving.value = false
  }
}

async function cancelAccountRemovalRequest() {
  accountRemovalSaving.value = true
  accountRemovalError.value = ''
  try {
    const response = await fetch('/api/v1/auth/account/removal-request', {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...authHeaders(),
      },
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      if (redirectToLoginAfterUnauthorized(response)) {
        return
      }
      accountRemovalError.value = accountApiError(payload, 'Unable to cancel account removal.')
      return
    }
    accountRemovalRequestedAt.value = null
    accountRemovalReason.value = ''
  } catch {
    accountRemovalError.value = 'Unable to cancel account removal.'
  } finally {
    accountRemovalSaving.value = false
  }
}

function watchedFilmsUrl(page) {
  const params = new URLSearchParams({ page: String(page) })
  const requestSortMode = route.query.sort
    ? selectedActivitySort(route.query.sort, currentFilmStatus.value)
    : currentSortMode.value
  if (isPublicProfile.value) {
    const username = encodeURIComponent(publicUsername.value)
    if (currentContentType.value === 'episodes') {
      if (requestSortMode !== 'last-added') {
        params.set('sort', requestSortMode)
      }
      if (selectedWatchedShow.value) {
        params.set('show', selectedWatchedShow.value)
      }
      return `/api/v1/home/public/${username}/episodes/watched?${params.toString()}`
    }

    if (requestSortMode !== 'last-added') {
      params.set('sort', requestSortMode)
    }
    if (selectedWatchedYear.value) {
      params.set('year', selectedWatchedYear.value)
    }
    return `/api/v1/home/public/${username}/films/watched?${params.toString()}`
  }

  const homeApiPrefix = isDemoProfile.value ? '/api/v1/home/demo' : '/api/v1/home'
  if (currentContentType.value === 'episodes') {
    if (requestSortMode !== 'last-added') {
      params.set('sort', requestSortMode)
    }
    if (currentFilmStatus.value === 'rated') {
      if (selectedWatchedShow.value) {
        params.set('show', selectedWatchedShow.value)
      }
      if (selectedRatedRating.value) {
        params.set('rating', selectedRatedRating.value)
      }
      return `${homeApiPrefix}/episodes/rated?${params.toString()}`
    }
    if (currentFilmStatus.value === 'favourited') {
      if (selectedWatchedShow.value) {
        params.set('show', selectedWatchedShow.value)
      }
      return `${homeApiPrefix}/episodes/favourited?${params.toString()}`
    }
    if (currentFilmStatus.value === 'next') {
      return `${homeApiPrefix}/episodes/next?${params.toString()}`
    }
    if (selectedWatchedShow.value) {
      params.set('show', selectedWatchedShow.value)
    }
    if (selectedWatchedYear.value) {
      params.set('year', selectedWatchedYear.value)
    }
    if (selectedWatchedMonth.value) {
      params.set('month', selectedWatchedMonth.value)
    }
    if (selectedWatchedFormat.value) {
      params.set('watch_format', selectedWatchedFormat.value)
    }
    return `${homeApiPrefix}/tv/watched?${params.toString()}`
  }
  if (requestSortMode !== 'last-added') {
    params.set('sort', requestSortMode)
  }
  if (ratingFiltersVisible.value && selectedRatedRating.value) {
    params.set('rating', selectedRatedRating.value)
  }

  if (currentContentType.value === 'shows') {
    return `${homeApiPrefix}/shows/${homeApiStatusSegment()}?${params.toString()}`
  }
  if (currentContentType.value === 'seasons') {
    return `${homeApiPrefix}/seasons/favourited?${params.toString()}`
  }
  if (currentContentType.value === 'people') {
    return `${homeApiPrefix}/people/favourited?${params.toString()}`
  }

  if (currentFilmStatus.value === 'rated') {
    return `${homeApiPrefix}/films/rated?${params.toString()}`
  }
  if (currentFilmStatus.value === 'favourited') {
    return `${homeApiPrefix}/films/favourited?${params.toString()}`
  }
  if (currentFilmStatus.value === 'watchlisted') {
    return `${homeApiPrefix}/films/watchlisted?${params.toString()}`
  }
  if (selectedWatchedYear.value) {
    params.set('year', selectedWatchedYear.value)
  }
  if (selectedWatchedMonth.value) {
    params.set('month', selectedWatchedMonth.value)
  }
  if (selectedWatchedFormat.value) {
    params.set('watch_format', selectedWatchedFormat.value)
  }
  return `${homeApiPrefix}/films/watched?${params.toString()}`
}

function homeFilmPath(status = currentFilmStatus.value, filterA = '', filterB = '') {
  return homeContentPath('films', status, filterA, filterB)
}

function homeContentPath(contentType = currentContentType.value, status = '', filterA = '', filterB = '') {
  if (isPublicProfile.value) {
    if (contentType === 'lists') {
      return homeListsPath('film', 'last-updated')
    }

    const username = encodeURIComponent(publicUsername.value)
    const publicContentType = contentType === 'episodes' ? 'episodes' : 'films'
    const parts = [`/filmsomniacs/${username}`, publicContentType, 'watched']
    if (publicContentType === 'films' && filterA) {
      parts.push(String(filterA))
    }
    return parts.join('/')
  }

  if (isDemoProfile.value) {
    if (contentType === 'lists') {
      return homeListsPath('film', 'last-updated')
    }

    const statusSegment = filmStatusPathSegment[status || defaultStatusByContentType[contentType]] || 'watched'
    const parts = ['/filmsomniacs/demo-account', contentType, statusSegment]
    if (filterA) {
      parts.push(String(filterA))
    }
    if (filterA && filterB) {
      parts.push(String(filterB))
    }
    return parts.join('/')
  }

  if (contentType === 'lists') {
    return homeListsPath('film', 'last-updated')
  }

  const statusSegment = filmStatusPathSegment[status || defaultStatusByContentType[contentType]] || 'watched'
  const parts = ['/home', contentType, statusSegment]
  if (filterA) {
    parts.push(String(filterA))
  }
  if (filterA && filterB) {
    parts.push(String(filterB))
  }
  return parts.join('/')
}

function homeListsPath(listType = selectedHomeListTypeKey.value, sortMode = currentSortMode.value) {
  const params = new URLSearchParams()
  if (listType !== 'film') {
    params.set('type', listType)
  }
  if (sortMode !== 'last-updated') {
    params.set('sort', sortMode)
  }
  const queryString = params.toString()
  const basePath = isPublicProfile.value
    ? `/filmsomniacs/${encodeURIComponent(publicUsername.value)}/lists`
    : isDemoProfile.value
    ? '/filmsomniacs/demo-account/lists'
    : '/home/lists'
  return queryString ? `${basePath}?${queryString}` : basePath
}

function homeListItemCountLabel(listItem) {
  const labels = homeListItemCountLabels[listItem?.type] || { singular: 'item', plural: 'items' }
  return Number(listItem?.item_count) === 1 ? labels.singular : labels.plural
}

function consumeListDeletedNotice() {
  if (currentContentType.value !== 'lists' || isReadOnlyProfile.value) {
    listDeletedNotice.value = ''
    return
  }

  try {
    const rawNotice = window.sessionStorage.getItem(listDeletedNoticeStorageKey)
    if (!rawNotice) {
      listDeletedNotice.value = ''
      return
    }

    window.sessionStorage.removeItem(listDeletedNoticeStorageKey)
    const parsedNotice = JSON.parse(rawNotice)
    listDeletedNotice.value = typeof parsedNotice?.message === 'string'
      ? parsedNotice.message
      : ''
  } catch {
    listDeletedNotice.value = ''
  }
}

function homeApiStatusSegment() {
  return {
    favourited: 'favourited',
    rated: 'rated',
    watchlisted: 'watchlisted',
  }[currentFilmStatus.value] || 'rated'
}

function syncFilmStateFromRoute() {
  const routeStatus = String(route.params.itemStatus || route.params.filmStatus || '')
  const fallbackStatus = defaultStatusByContentType[currentContentType.value] || 'watched'
  const nextStatus = filmStatusFromPath[routeStatus] || fallbackStatus
  const filterA = String(route.params.filterA || '')
  const filterB = String(route.params.filterB || '')
  const routeShow = String(route.query.show || '')

  if (currentContentType.value === 'lists') {
    selectedHomeListTypeKey.value = homeListTypes.some((listType) => listType.key === route.query.type)
      ? String(route.query.type)
      : 'film'
    currentSortMode.value = selectedHomeListSort(route.query.sort)
    currentFilmStatus.value = ''
    selectedWatchedShow.value = ''
    selectedWatchedYear.value = ''
    selectedWatchedMonth.value = ''
    selectedWatchedFormat.value = ''
    selectedRatedRating.value = ''
    return
  }

  if (isPublicProfile.value) {
    currentFilmStatus.value = 'watched'
    currentSortMode.value = selectedWatchedSort(route.query.sort)
    if (Object.prototype.hasOwnProperty.call(route.query, 'view')) {
      homeViewMode.value = selectedHomeViewMode(route.query.view)
    }
    selectedWatchedShow.value = currentContentType.value === 'episodes' ? routeShow : ''
    selectedWatchedYear.value = currentContentType.value === 'films' && /^\d{4}$/.test(filterA)
      ? filterA
      : ''
    selectedWatchedMonth.value = ''
    selectedWatchedFormat.value = ''
    selectedRatedRating.value = ''
    return
  }

  currentFilmStatus.value = nextStatus
  if (isDemoProfile.value) {
    currentSortMode.value = selectedActivitySort(route.query.sort, nextStatus)
    if (Object.prototype.hasOwnProperty.call(route.query, 'view')) {
      homeViewMode.value = selectedHomeViewMode(route.query.view)
    }
  } else if (Object.prototype.hasOwnProperty.call(route.query, 'view')) {
    homeViewMode.value = selectedHomeViewMode(route.query.view)
  }
  selectedWatchedShow.value = currentContentType.value === 'episodes' &&
    ['watched', 'rated', 'favourited'].includes(nextStatus)
    ? routeShow
    : ''
  if (nextStatus === 'watched' && ['films', 'episodes'].includes(currentContentType.value)) {
    selectedWatchedYear.value = filterA
    selectedWatchedMonth.value = filterA ? filterB : ''
    selectedWatchedFormat.value = ['films', 'episodes'].includes(currentContentType.value)
      ? selectedWatchedFilmFormat(route.query.format)
      : ''
    selectedRatedRating.value = ''
    return
  }

  selectedWatchedYear.value = ''
  selectedWatchedMonth.value = ''
  selectedWatchedFormat.value = ''
  selectedRatedRating.value = nextStatus === 'rated' &&
    ['films', 'shows', 'episodes'].includes(currentContentType.value)
    ? filterA
    : ''
}

function selectedHomeListSort(rawSort) {
  const sort = Array.isArray(rawSort) ? rawSort[0] : rawSort
  const value = String(sort || '').trim()
  return homeListSortOptions.has(value) ? value : 'last-updated'
}

function selectedWatchedSort(rawSort) {
  const sort = Array.isArray(rawSort) ? rawSort[0] : rawSort
  const value = String(sort || '').trim()
  const options = currentContentType.value === 'episodes'
    ? episodeWatchedSortOptions
    : watchedSortOptions
  return options.has(value) ? value : 'last-added'
}

function selectedWatchedFilmFormat(rawFormat) {
  const value = Array.isArray(rawFormat) ? rawFormat[0] : rawFormat
  return String(value || '').trim()
}

function watchedFormatFilterLabel(formatFilter) {
  if (!formatFilter?.format) {
    return formatFilter?.label || 'All'
  }
  return `${formatFilter.label} (${formatFilter.count})`
}

function selectedHomeViewMode(rawViewMode) {
  const viewMode = Array.isArray(rawViewMode) ? rawViewMode[0] : rawViewMode
  const value = String(viewMode || '').trim()
  return homeViewModes.includes(value) ? value : '2'
}

function selectedActivitySort(rawSort, status) {
  const sort = Array.isArray(rawSort) ? rawSort[0] : rawSort
  const value = String(sort || '').trim()
  if (status === 'next') {
    return nextEpisodeSortOptions.has(value) ? value : 'recently-watched'
  }
  if (status === 'watched') {
    const options = currentContentType.value === 'episodes'
      ? episodeWatchedSortOptions
      : watchedSortOptions
    return options.has(value) ? value : 'last-added'
  }
  const options = currentContentType.value === 'episodes'
    ? episodeRatedSortOptions
    : ratedSortOptions
  return options.has(value) ? value : 'last-added'
}

function pageFromRoute() {
  const rawPage = Array.isArray(route.query.page) ? route.query.page[0] : route.query.page
  const page = Number.parseInt(String(rawPage || ''), 10)
  return Number.isFinite(page) && page > 0 ? page : 1
}

function routeQueryWithPage(page) {
  const query = { ...route.query }
  if (page > 1) {
    query.page = String(page)
  } else {
    delete query.page
  }
  return query
}

function routeQueryWithHomeListSort(sortMode, page = 1) {
  const query = routeQueryWithPage(page)
  if (sortMode !== 'last-updated') {
    query.sort = sortMode
  } else {
    delete query.sort
  }
  return query
}

function routeQueryWithWatchedSort(sortMode, page = 1) {
  const query = routeQueryWithPage(page)
  if (currentContentType.value !== 'episodes') {
    delete query.show
  }
  if (sortMode !== 'last-added') {
    query.sort = sortMode
  } else {
    delete query.sort
  }
  if (homeViewMode.value !== '2') {
    query.view = homeViewMode.value
  } else {
    delete query.view
  }
  return query
}

function routeQueryWithWatchedFormat(format, page = 1) {
  const query = routeQueryWithPage(page)
  if (format) {
    query.format = format
  } else {
    delete query.format
  }
  return query
}

function resetRoutePageAndLoadFirstPage() {
  if (pageFromRoute() > 1) {
    router.push({
      path: route.path,
      query: routeQueryWithPage(1),
    })
    return
  }
  loadWatchedFilms(1)
}

function goToWatchedFilmsPage(page) {
  if (
    page < 1 ||
    page > watchedFilmsNumPages.value ||
    page === watchedFilmsPage.value ||
    watchedFilmsLoading.value
  ) {
    return
  }
  router.push({
    path: route.path,
    query: routeQueryWithPage(page),
  })
}

function scrollToPageStart() {
  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }
}

async function goToHomeListsPage(page) {
  if (
    page < 1 ||
    page > homeListsNumPages.value ||
    page === homeListsPage.value ||
    homeListsLoading.value
  ) {
    return
  }
  await router.push({
    path: route.path,
    query: routeQueryWithHomeListSort(currentSortMode.value, page),
  })
  scrollToPageStart()
}

async function setHomeViewMode(viewMode) {
  if (!homeViewModes.includes(viewMode)) {
    return
  }
  if (homeViewMode.value === viewMode) {
    return
  }
  homeViewMode.value = viewMode
  if (homeViewModeControlsVisible.value) {
    await router.push({
      path: route.path,
      query: routeQueryWithWatchedSort(currentSortMode.value, pageFromRoute()),
    })
  }
}

function isLastHomeItem(item) {
  return watchedFilms.value.indexOf(item) === watchedFilms.value.length - 1
}

function homeItemKey(item) {
  return item?.watch_id ||
    item?.rating_id ||
    item?.favourite_id ||
    item?.watchlist_id ||
    item?.watchlist_item_id ||
    item?.id
}

function setWatchedYearFilter(year, closeFiltersModal = true) {
  if (closeFiltersModal) {
    closeHomeFiltersModal()
  }
  const path = year
    ? homeContentPath(currentContentType.value, 'watched', year)
    : homeContentPath(currentContentType.value, 'watched')
  router.push(homeWatchedFilterLocation(path))
}

function setSortMode(sortMode) {
  if (!sortButtons.value.some((button) => button.key === sortMode)) {
    return
  }
  currentSortMode.value = sortMode
  selectedWatchedYear.value = ''
  selectedWatchedMonth.value = ''
  if (currentContentType.value === 'lists') {
    router.push({
      path: route.path,
      query: routeQueryWithHomeListSort(sortMode, 1),
    })
    return
  }
  router.push({
    path: currentSortRoutePath(),
    query: routeQueryWithWatchedSort(sortMode, 1),
  })
}

function setWatchedFormatFilter(format, closeFiltersModal = true) {
  if (closeFiltersModal) {
    closeHomeFiltersModal()
  }
  router.push({
    path: route.path,
    query: routeQueryWithWatchedFormat(format, 1),
  })
}

function currentSortRoutePath() {
  if (
    currentFilmStatus.value === 'rated' &&
    ['films', 'shows', 'episodes'].includes(currentContentType.value) &&
    selectedRatedRating.value
  ) {
    return homeContentPath(currentContentType.value, 'rated', selectedRatedRating.value)
  }
  return homeContentPath(currentContentType.value, currentFilmStatus.value)
}

function setFilmStatus(status, closeFiltersModal = true) {
  if (!statusFilters.value.some((statusFilter) => statusFilter.key === status)) {
    return
  }
  currentSortMode.value = 'last-added'
  if (closeFiltersModal) {
    closeHomeFiltersModal()
  }
  closeWatchedShowDropdown()
  router.push(homeContentPath(currentContentType.value, status))
}

async function setContentType(contentType) {
  currentSortMode.value = 'last-added'
  closeWatchedShowDropdown()
  await router.push(homeContentPath(contentType))
}

function setHomeListType(listType, closeFiltersModal = true) {
  if (!homeListTypes.some((item) => item.key === listType)) {
    return
  }
  currentSortMode.value = 'last-updated'
  if (closeFiltersModal) {
    closeHomeFiltersModal()
  }
  closeWatchedShowDropdown()
  router.push(homeListsPath(listType, 'last-updated'))
}

function openHomeFiltersModal() {
  homeFiltersModalOpen.value = true
}

function closeHomeFiltersModal() {
  homeFiltersModalOpen.value = false
}

function openHomeListModal(listType = selectedHomeListTypeKey.value) {
  homeListModalTypeKey.value = homeListTypes.some((item) => item.key === listType) ? listType : 'film'
  homeListForm.value = {
    title: '',
    description: '',
    public: true,
  }
  homeListModalError.value = ''
  homeListModalOpen.value = true
}

function closeHomeListModal() {
  if (homeListModalSaving.value) {
    return
  }
  homeListModalOpen.value = false
  homeListModalError.value = ''
}

function setSettingsPanel(panel) {
  router.push(panel === 'account-removal' ? '/home/settings?panel=account-removal' : '/home/settings')
}

function handleSettingsLinkClick(link, event) {
  if (link.external) {
    return
  }
  event.preventDefault()
  if (link.key === 'google-disconnect') {
    disconnectGoogleAccount()
    return
  }
  setSettingsPanel(link.key)
}

async function submitHomeListCreate() {
  const title = homeListForm.value.title.trim()
  if (!title) {
    homeListModalError.value = 'Title is required.'
    return
  }

  homeListModalSaving.value = true
  homeListModalError.value = ''
  try {
    const response = await fetch('/api/v1/home/lists', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      body: JSON.stringify({
        type: homeListModalTypeKey.value,
        title,
        description: homeListForm.value.description.trim(),
        public: homeListForm.value.public,
      }),
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      if (redirectToLoginAfterUnauthorized(response)) {
        return
      }
      homeListModalError.value = payload.title || 'Unable to add list.'
      return
    }
    if (homeListModalTypeKey.value !== selectedHomeListTypeKey.value) {
      await router.push(homeListsPath(homeListModalTypeKey.value))
    } else {
      await loadHomeLists()
    }
    homeListModalOpen.value = false
  } catch {
    homeListModalError.value = 'Unable to add list.'
  } finally {
    homeListModalSaving.value = false
  }
}

function setWatchedShowFilter(show, closeFiltersModal = true) {
  selectedWatchedShow.value = show
  selectedWatchedYear.value = ''
  selectedWatchedMonth.value = ''
  if (closeFiltersModal) {
    closeHomeFiltersModal()
  }
  closeWatchedShowDropdown()
  if (showFiltersVisible.value) {
    const path = currentFilmStatus.value === 'watched'
      ? homeContentPath('episodes', 'watched')
      : homeContentPath('episodes', currentFilmStatus.value)
    router.push({
      path,
      query: show ? { show } : {},
    })
    return
  }
  resetRoutePageAndLoadFirstPage()
}

function toggleWatchedShowDropdown() {
  watchedShowDropdownOpen.value = !watchedShowDropdownOpen.value
  if (watchedShowDropdownOpen.value) {
    watchedShowDropdownQuery.value = ''
  }
}

function closeWatchedShowDropdown() {
  watchedShowDropdownOpen.value = false
  watchedShowDropdownQuery.value = ''
}

function selectWatchedShowFromDropdown(show, closeFiltersModal = true) {
  setWatchedShowFilter(show, closeFiltersModal)
}

function setWatchedMonthFilter(month, closeFiltersModal = true) {
  if (!selectedWatchedYear.value) {
    return
  }
  if (closeFiltersModal) {
    closeHomeFiltersModal()
  }
  router.push(homeWatchedFilterLocation(
    homeContentPath(currentContentType.value, 'watched', selectedWatchedYear.value, month),
  ))
}

function homeWatchedFilterLocation(path) {
  const query = routeQueryWithWatchedSort(currentSortMode.value, 1)
  if (currentContentType.value === 'episodes' && selectedWatchedShow.value) {
    query.show = selectedWatchedShow.value
  }
  return { path, query }
}

function homeWatchedFilterHref(path) {
  return router.resolve(homeWatchedFilterLocation(path)).href
}

function homeWatchedFormatFilterHref(format) {
  return router.resolve({
    path: route.path,
    query: routeQueryWithWatchedFormat(format, pageFromRoute()),
  }).href
}

function setRatedRatingFilter(rating, closeFiltersModal = true) {
  if (closeFiltersModal) {
    closeHomeFiltersModal()
  }
  const path = rating
    ? homeContentPath(currentContentType.value, 'rated', rating)
    : homeContentPath(currentContentType.value, 'rated')
  router.push(
    currentContentType.value === 'episodes' && selectedWatchedShow.value
      ? { path, query: { show: selectedWatchedShow.value } }
      : path,
  )
}

function visibleMonthFilters(year) {
  if (isPublicProfile.value) {
    return []
  }
  return selectedWatchedYear.value === year ? watchedMonthFilters.value : []
}

function posterImageUrl(film) {
  if (film?.poster_url) {
    return film.poster_url
  }
  return currentMedium.value === 'tv' ? episodePlaceholderImage : posterPlaceholderImage
}

function isPlaceholderPoster(film) {
  if (!film?.poster_url) {
    return true
  }
  return String(film.poster_url).endsWith('placeholder-film-camera.svg') ||
    String(film.poster_url).endsWith('placeholder-film.svg') ||
    String(film.poster_url).endsWith('placeholder-episode.svg') ||
    String(film.poster_url).endsWith('placeholder-episode-tv.svg')
}

function shouldShowPlaceholderOverlay(film) {
  return currentMedium.value !== 'tv' && isPlaceholderPoster(film)
}

function itemImageAlt(item) {
  return currentMedium.value === 'tv'
    ? `${item.title} episode image`
    : `${item.title} poster`
}

function episodeCode(item) {
  const seasonNumber = Number.parseInt(item?.season_number, 10)
  const episodeNumber = Number.parseInt(item?.episode_number, 10)
  if (!Number.isFinite(seasonNumber) || !Number.isFinite(episodeNumber)) {
    return ''
  }

  const season = seasonNumber < 10 ? `0${seasonNumber}` : String(seasonNumber)
  const episode = episodeNumber < 10 ? `0${episodeNumber}` : String(episodeNumber)
  return `${season}x${episode}`
}

function episodeTitleLine(item) {
  return [episodeCode(item), item?.title || ''].filter(Boolean).join(' ')
}

function watchedDateLabel() {
  return currentMedium.value === 'tv' && currentSortMode.value.startsWith('watch-date')
    ? 'Watched'
    : 'Added'
}

function watchedEpisodeDetailDate(item) {
  const date = watchedOverlayDate(item)
  return date ? `${watchedDateLabel()}: ${date}` : ''
}

function formatPartialWatchDate(value) {
  const rawValue = String(value || '').trim()
  if (!rawValue) {
    return ''
  }

  const year = rawValue.slice(0, 4)
  if (!/^\d{4}$/.test(year)) {
    return ''
  }

  const month = rawValue.length >= 7 ? rawValue.slice(5, 7) : ''
  if (!month) {
    return year
  }
  if (!/^\d{2}$/.test(month)) {
    return year
  }
  const monthNumber = Number.parseInt(month, 10)
  const monthLabels = [
    '',
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  if (monthNumber < 1 || monthNumber > 12) {
    return year
  }

  const day = rawValue.length >= 10 ? rawValue.slice(8, 10) : ''
  if (day && /^\d{2}$/.test(day)) {
    const dayNumber = Number.parseInt(day, 10)
    if (dayNumber >= 1 && dayNumber <= 31) {
      return `${monthLabels[monthNumber]} ${dayNumber}, ${year}`
    }
  }

  return `${monthLabels[monthNumber]} ${year}`
}

function hasCompleteWatchDate(value) {
  const rawValue = String(value || '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
    return false
  }
  const month = Number.parseInt(rawValue.slice(5, 7), 10)
  const day = Number.parseInt(rawValue.slice(8, 10), 10)
  return month >= 1 && month <= 12 && day >= 1 && day <= 31
}

function watchedPartialDate(item) {
  return formatPartialWatchDate(item?.latest_watched)
}

function watchedOverlayDate(item) {
  return item?.overlay_date || watchedPartialDate(item)
}

function watchedDetailDate(item) {
  if (currentMedium.value === 'tv') {
    return watchedEpisodeDetailDate(item)
  }
  const partialDate = watchedPartialDate(item)
  if (partialDate && !hasCompleteWatchDate(item?.latest_watched)) {
    return partialDate
  }
  return item?.watch_date_display || watchedOverlayDate(item) || ''
}

function watchedTextMeta(item) {
  if (currentMedium.value === 'tv') {
    return watchedEpisodeDetailDate(item)
  }
  return [watchedDetailDate(item), item?.format || ''].filter(Boolean).join(' • ')
}

function ratedDetailDate(item) {
  return item?.rating_date_display ? `Rated: ${item.rating_date_display}` : ''
}

function favouritedDetailDate(item) {
  return item?.favourite_date_display ? `Favourited: ${item.favourite_date_display}` : ''
}

function nextDetailDate(item) {
  return item?.air_date_display || ''
}

function watchlistedDetailDate(item) {
  return item?.watchlist_date_display ? `Watchlisted: ${item.watchlist_date_display}` : ''
}

function homeDetailDate(item) {
  return {
    favourited: favouritedDetailDate,
    next: nextDetailDate,
    rated: ratedDetailDate,
    watched: watchedDetailDate,
    watchlisted: watchlistedDetailDate,
  }[currentFilmStatus.value]?.(item) || ''
}

function homeTextMeta(item) {
  return {
    favourited: favouritedDetailDate,
    next: nextDetailDate,
    rated: ratedDetailDate,
    watched: watchedTextMeta,
    watchlisted: watchlistedDetailDate,
  }[currentFilmStatus.value]?.(item) || ''
}

function mediumPath(section = '') {
  const base = '/home'
  return section ? `${base}/${section}` : base
}

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}

watch(
  () => ({
    fullPath: route.fullPath,
    path: route.path,
    show: route.query.show,
  }),
  (currentRouteState, previousRouteState) => {
    if (isReadOnlyProfile.value) {
      loadPublicProfile()
    } else {
      updateHomeDocumentTitle()
    }
    loadWatchCounts()
    if (isSettingsSection.value) {
      loadAccountSettings()
      return
    }
    syncFilmStateFromRoute()
    consumeListDeletedNotice()
    if (
      !previousRouteState ||
      currentRouteState.path !== previousRouteState.path ||
      currentRouteState.show !== previousRouteState.show
    ) {
      closeWatchedShowDropdown()
    }
    loadWatchedFilms(pageFromRoute())
  },
  { immediate: true },
)

useMetaDescription(() => buildMetaDescription(
  'profile',
  isReadOnlyProfile.value ? { username: readOnlyProfileDisplayName.value } : {},
))
</script>
